// EmbedPage — 협업 앱(메신저/드라이브/오피스/캘린더/메일/협업도구) 같은오리진 iframe 임베드 + SSO 세션 처리.
import { useRef, useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import Markdown from '../components/Markdown';
import ReadReceiptModal from '../components/ReadReceiptModal';
import StickerPicker from '../components/StickerPicker';
import OfficePreviewModal from '../components/OfficePreviewModal';
import { NC_APPS, NC_HDR, autoConnectMattermost } from '../utils/embedCollab';
import { subscribeEmbedAuth, subscribeEmbedReload } from '../utils/embedReload';
import { ensureNcSession, getGatewayUserId } from '../lib/ncSso';
import {
  causeReason, causeText, claimNcReload, classifyEmbedError, currentNcRecovery, detectFrameProblem,
  embedErrorReason, ensureGatewayToken, isLoginDocument, issueMail2Ticket, probeMmState, reauthGuard, relogin,
  reportEmbedAuthEvent, serviceOf, trackNcRecovery,
} from '../lib/embedAuth';
import { setupDriveOfficeNewWindow } from '../utils/driveOfficeNewWindow';
import { setupDriveActions } from '../utils/driveHeaderActions';
import { withEulReul } from '../utils/koreanParticle';
import { openInNewProcess } from '../utils/openNoopener';
import { setupMmFloatingDate } from '../utils/mmFloatingDate';
import { applyMmModalCss } from '../utils/mmModalCss';
import { applyMmPostEditCss } from '../utils/mmPostEditCss';
import { applyMmStickerSizeCss } from '../utils/mmStickerSizeCss';
import { applyMmStickerOrderCss } from '../utils/mmStickerOrderCss';
import { applyMmReadBadgeCss } from '../utils/mmReadBadgeCss';
import { applyMmFSkin } from '../utils/collabSkinMm';
import { applyNcFSkin } from '../utils/collabSkinNc';
import { applyRcFSkin } from '../utils/collabSkinRc';
import { setupMmMenuBackClose } from '../utils/mmMenuBackClose';
import { setupMmStickerButton, setupMmStickerStage } from '../utils/mmSticker';
import { setupMmStickerFileFilter } from '../utils/mmStickerFileFilter';
import { setupMmZoomLabel } from '../utils/mmZoomLabel';
import { setupMmPreviewTouch } from '../utils/mmPreviewTouch';
import { setupMmProfileExtra } from '../utils/mmProfileExtra';
import { setupMmExcelPaste } from '../utils/mmExcelPaste';
import { setupMmOfficePreviewButton } from '../utils/mmOfficePreview';
import { setupMmThreadUnread } from '../utils/mmThreadUnread';
import { muteMmNotifications } from '../messengerOnScreen';
import { watchMmReadCalls } from '../collabUnreadStore';
import { mmPermalink, mmPostIdFromSearch, mmSsoUrlForPost, stripMmPostParam } from '../utils/mmPostLink';
import useIsMobile from '../hooks/useIsMobile';
import { useSplitView } from '../components/SplitShell';
import { mmAPI } from '../api/client';
import { IconAlertTriangle, IconMailOpened, IconPencil, IconSparkles } from '@tabler/icons-react';
import { COLORS } from '../utils/uiStyles';
import { BOT_NAME } from '../utils/brand';

// 이 화면이 NC 복구 결과를 기다리는 최대 시간 — 넘으면 멈춘 기다림으로 보고 새 복구를 허용한다
// (ensureNcSession 은 20초 상한 + 확인 8초라 정상이면 그 안에 끝난다 · lib/embedAuth 기록과 같은 값).
const NC_WAIT_MAX_MS = 60000;
const AI_BUTTON_ICON ='<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6L12 3z"/><path d="M5 3v4"/><path d="M3 5h4"/><path d="M19 17v4"/><path d="M17 19h4"/></svg>';
const MM_PROVISION_MESSAGE = '메신저 계정 준비 중입니다. 잠시 후 다시 시도해 주세요. 계속되면 관리자에게 문의해 주세요.';
// 시안 F 메신저 피부(#2014)의 1100 구간 = 게이트웨이 «창» 폭 901~1200(시안 줄 26). iframe 폭이 아니다 —
// 게이트웨이 사이드바 접힘·끈 폭·분할 보기로 iframe 폭은 따로 논다.
const GW_MID_QUERY = '(min-width:901px) and (max-width:1200px)';
// 🔴 jsdom 에는 window.matchMedia 가 없다 — 던지면 같은 줄 뒤 메신저 보정(스티커·읽음 등)이 try 안에서 전부 건너뛰어진다.
function gwMidMatches() {
  try { return typeof window.matchMedia === 'function' && !!window.matchMedia(GW_MID_QUERY).matches; } catch (e) { return false; }
}

// 협업 앱(메신저/드라이브) 셸 임베드 — 같은 오리진 iframe.
// ⚠️ MM/NC는 자체 세션 쿠키라, 다른 게이트웨이 계정으로 바뀌어도 옛 세션이 남으면 그 사용자로 보임.
// → 마운트 시 그 앱의 "현재 로그인 사용자"를 확인:
//    - 세션 없음(401/계정없음, 신규 사용자) → 바로 SSO(로그아웃 댄스 불필요).
//    - 같은 사용자 → appRoot로 바로(재SSO 없음).
//    - 다른 사용자 세션 → (MM)SSO가 세션 전환 / (NC)옛 세션 로그아웃 후 SSO.
// hideCss: iframe 안에 주입할 CSS(로딩로고/특정요소 숨김).
// tabs: [{key,label,url}] 주면 상단 탭바로 여러 NC 앱을 한 메뉴에서 전환(협업 도구=칸반/테이블/위키).
// NC_APPS·NC_HDR(앱별 헤더 라벨)·autoConnectMattermost(MM OAuth 자동연결)는 utils/embedCollab 로 분리(상단 import).

export function EmbedContent({ app, title, appRoot, ssoUrl, hideCss, tabs, keepAliveActive = true }) {
  const MAX_SSO_RETRIES = 3;
  const MAX_TEAM_TRIES = 4;
  const ref = useRef(null);
  const activeRef = useRef(keepAliveActive);
  // 모바일 메신저 팝업 뒤로가기 게이트 — 모바일이고 «화면 분할이 아닐 때»만 켠다.
  // 🔴 분할(폭 ≥1100)에서는 옆 칸 메신저도 좁아 MM 이 모바일 메뉴를 쓰는데, 거기서 우리가 pushState·back 을
  //    하면 본 칸(ChatPage 등)의 popstate 처리까지 흔들린다 → sideApp 이 있으면 끈다.
  const isMobile = useIsMobile();
  const split = useSplitView();
  const backCloseOkRef = useRef(false);
  useEffect(() => { backCloseOkRef.current = !!isMobile && !split.sideApp; }, [isMobile, split.sideApp]);
  const [activeTab, setActiveTab] = useState(() => (tabs && tabs.length ? tabs[0].key : null));
  const effRoot = (tabs && tabs.length) ? ((tabs.find((t) => t.key === activeTab) || tabs[0]).url) : appRoot;
  const ssoTries = useRef(0);
  const teamTries = useRef(0);
  const mmEnsureTriedRef = useRef(false);
  const mmEnsureRunningRef = useRef(false);
  const ssoTimersRef = useRef([]);
  const teamTimersRef = useRef([]);
  const mailSetupRedirected = useRef(false);
  const aiNameRef = useRef(BOT_NAME);  // 드라이브 AI(NC Assistant) 표시 이름(관리자 설정)
  // 관리자는 MM/NC 원본 메뉴 전체 노출(숨김 미적용). 단 메신저 로딩 로고는 전원 숨김.
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'admin'; } catch (e) { return false; } })();
  const [src, setSrc] = useState('');
  const [loadErr, setLoadErr] = useState(false);  // iframe 로드 실패(MM/NC 다운·SSO실패 등) → "불러올 수 없습니다" UI
  const [mmProvisionFailed, setMmProvisionFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);    // 실제 앱 화면이 떴는지(SSO/로그인 중간단계엔 false=로딩 오버레이 유지 → 첫진입 깨진 빈프레임 가림)
  const [reloadSeq, setReloadSeq] = useState(0);  // 외부 요청으로 iframe 을 강제 재로드한 횟수(관측용 표식 — iframe data 속성으로 노출)
  // 자동 재인증(docs/embed_auth_expiry.md ⑥): 실패 원인 {cause} — 실패 카드(메일2는 배너)의 원인 줄.
  const [authFail, setAuthFail] = useState(null);
  const authFailRef = useRef(null);        // 같은 원인을 거듭 보고하지 않게(포커스마다 recoverIfDead 가 돈다)
  const pendingReauthRef = useRef(null);   // {reason, report} — 재인증 뒤 다시 불러오는 중(다음 load 가 결과다)
  const ncWaitRef = useRef(null);          // {recovery, at} — 이 화면이 기다리는 NC 복구(겹친 신호는 같은 결과를 쓴다)
  const mail2ReadyRef = useRef(undefined); // 메일2 자동 로그인 가능 여부(마운트 티켓 응답의 ready) — false 면 로그인 화면이 정상
  const mail2EmailRef = useRef('');        // 메일2 아이디 힌트(_user)
  const loadTimerRef = useRef(null);
  const obsRef = useRef([]);     // iframe doc MutationObserver 목록(부하감사 F1: 언마운트 시 disconnect)
  const docCleanupRef = useRef([]);   // iframe doc 보정 dispose 목록(MM 날짜배지 — 옵저버 + scroll 리스너)
  // 메신저 '읽음 표시'(JANDI식): 라벨은 MM iframe 안에 주입하고, 팝업은 iframe 밖에서 우리가 그린다.
  const receiptsRef = useRef({});          // postId -> {read_count, unread_count, read[], unread[], at}
  const receiptBusyRef = useRef(false);    // 조회 중복 방지(스크롤마다 부르면 MM DB 를 두들긴다)
  const receiptOffRef = useRef(false);     // 서버가 기능 꺼짐(enabled:false)이라고 하면 다시 안 부른다
  const [receiptView, setReceiptView] = useState(null);
  // 메신저 스티커 피커(J2b): 버튼은 MM iframe 안에 주입하고, 패널은 iframe 밖에서 우리가 그린다. null = 닫힘.
  const [stickerAnchor, setStickerAnchor] = useState(null);
  // 메신저 오피스 미리보기: 버튼은 MM 미리보기 창 안에 주입하고, 모달은 iframe 밖에서 우리가 그린다. null = 닫힘.
  const [officePreview, setOfficePreview] = useState(null);   // {fileId, fileName, ext}
  const pollRef = useRef(null);  // onLoad 헤더정리 폴링 인터벌(부하감사 F2: 재실행 시 이전 정리·언마운트 정리)
  // 메일: 글자크기·안읽음 토글(localStorage 유지). ref는 iframe CSS 주입 헬퍼가 최신값 읽도록.
  const [mailFontPx, setMailFontPx] = useState(() => { const v = parseInt(localStorage.getItem('mailFontPx'), 10); return (v >= 12 && v <= 22) ? v : 14; });
  const [mailUnreadOnly, setMailUnreadOnly] = useState(() => localStorage.getItem('mailUnreadOnly') === '1');
  // #112-①: 메일 본문(reading pane) 확대/축소(비례 zoom·localStorage). 본문은 중첩 iframe(message-frame)이라 별도 주입.
  const [mailMsgZoom, setMailMsgZoom] = useState(() => { const v = parseFloat(localStorage.getItem('mailMsgZoom')); return (v >= 1 && v <= 2.5) ? v : 1; });
  // #115 메일 AI 어시스트(본문 요약·답장 초안) 결과 패널: { mode:'summary'|'draft', loading, text, error }
  const [aiPanel, setAiPanel] = useState(null);
  const [aiCopied, setAiCopied] = useState(false);
  const mailFontRef = useRef(14);
  const mailUnreadRef = useRef(false);
  const mailMsgZoomRef = useRef(1);
  const msgObsRef = useRef(null);   // 메일 본문 iframe 등장 감지 옵저버(메일 열 때마다 재주입)

  // 🔴 낡은 NC 세션 쿠키가 남아 있으면 드라이브가 **계속** 깨진다(2026-08-25 실측).
  //    NC 가 그 쿠키로 세션을 못 살리고 새로 만들면서, OIDC 흐름이 세션에 넣어 둔 state 가 사라지고
  //    콜백이 "state has expired" 403 을 낸다 — 로그도 안 남는다. 재시도해도 같은 쿠키를 다시 보내
  //    또 실패한다(마스터는 두 번 다 실패, 시크릿창은 성공).
  //    그래서 **SSO 를 시작하기 전에 그 쿠키를 서버가 만료시킨다.** 실패해도 그냥 진행한다 —
  //    지금보다 나빠질 일은 없다.
  const resetNcCookies = () => {
    const token = localStorage.getItem('token');
    if (!token) return Promise.resolve();
    const API = process.env.REACT_APP_API_URL || '';
    return fetch(`${API}/api/collab/nc-session-reset`, {
      method: 'POST', credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => undefined).catch(() => undefined);
  };

  // 보이는 NC 화면이 로그인 문서면 재인증 1회(reauthNc — 숨은 SSO → 그 자리에서 다시 불러오기).
  // 60초 쿨다운·10분 3회 상한은 서비스 공용 guard(lib/embedAuth)가 센다 — 쿨다운이면 조용히 넘기고,
  // 상한이나 자동 재인증 실패 뒤라면 원인 카드로 바꾼다(예전 자체 카운터와 같은 의미).
  // 🔴 SSO 중간 경로(user_oidc)는 세션 죽음이 아니다(isLoginDocument 가 빼고 본다). 숨은 화면은 다시 열 때 본다.
  const recoverIfDead = (reason) => {
    if (!NC_APPS.includes(app)) return;
    if (!ssoUrl) return;
    if (!activeRef.current) return;
    const w = ref.current && ref.current.contentWindow;
    if (!w) return;
    if (!isLoginDocument(app, w)) return;
    reauthNc(reason || 'open_check', { quietCooldown: true });
  };

  const scheduleRecoverCheck = (reason) => {
    if (!NC_APPS.includes(app) || !ssoUrl) return;
    const t = setTimeout(() => { try { recoverIfDead(reason); } catch (e) { /* */ } }, 300);
    ssoTimersRef.current.push(t);
  };

  useEffect(() => {
    const wasActive = activeRef.current;
    activeRef.current = keepAliveActive;
    // g2786 — 칸이 숨으면(display:none 으로만 숨고 내려가지 않는다) 스티커 시트를 닫는다 — 열린 채 남으면
    //   <html data-gw-sheet="1"> 이 남아 다른 모든 화면에서 푸시 안내 띠가 숨는다.
    if (!keepAliveActive) setStickerAnchor(null);
    if (!wasActive && keepAliveActive) {
      scheduleRecoverCheck('open_check');
      // 드라이브: 숨어 있는 동안(옵저버가 건너뛴다) 첫 줄이 다시 그려져 떨어진 [AI·종] 묶음을 1회 되돌린다.
      if (app === 'drive') {
        try {
          const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
          if (d) cleanupDriveHeader(d);
        } catch (e) { /* 로드 중 접근불가 — 무시 */ }
      }
      // 다시 열 때(열기 직전 확인): 메신저는 세션이 만료됐을 때만, 메일2는 로그인 화면일 때만 재인증한다.
      if (app === 'messenger' && src) {
        probeMmState().then((state) => {
          if (state === 'expired' && activeRef.current) reauthEmbed('open_check');
        }).catch(() => {});
      }
      if (app === 'mail2' && src) reauthEmbed('open_check', { verify: true });
    }
  }, [keepAliveActive]);
  useEffect(() => {
    if (!NC_APPS.includes(app) || !ssoUrl || typeof window === 'undefined') return undefined;
    const handler = () => { if (activeRef.current) scheduleRecoverCheck('visibility_check'); };
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [app, ssoUrl]);
  useEffect(() => { mailFontRef.current = mailFontPx; }, [mailFontPx]);
  useEffect(() => { mailUnreadRef.current = mailUnreadOnly; }, [mailUnreadOnly]);
  useEffect(() => { mailMsgZoomRef.current = mailMsgZoom; }, [mailMsgZoom]);

  // src가 정해지면 로드 감시 타이머 시작. ~20초 내 onLoad가 안 오면 실패로 간주(무한 스피너 방지).
  useEffect(() => {
    if (!src) return;
    setLoadErr(false);
    if (app === 'messenger') setMmProvisionFailed(false);
    setLoaded(false);
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setLoadErr(true), 20000);
    return () => { if (loadTimerRef.current) clearTimeout(loadTimerRef.current); };
  }, [src]);

  // 언마운트 정리(부하감사 F1·F2): iframe MutationObserver·폴링 인터벌·로드타이머 누수 방지.
  // (탭 전환·로그아웃으로 EmbedPage가 빠질 때 옵저버/인터벌이 죽은 iframe doc을 계속 붙들지 않도록.)
  useEffect(() => () => {
    try { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; } catch (e) { /* */ }
    try { if (loadTimerRef.current) clearTimeout(loadTimerRef.current); loadTimerRef.current = null; } catch (e) { /* */ }
    try { ssoTimersRef.current.forEach((t) => clearTimeout(t)); ssoTimersRef.current = []; } catch (e) { /* */ }
    try { teamTimersRef.current.forEach((t) => clearTimeout(t)); teamTimersRef.current = []; } catch (e) { /* */ }
    try { obsRef.current.forEach((o) => { try { o.disconnect(); } catch (e) { /* */ } }); obsRef.current = []; } catch (e) { /* */ }
    try { docCleanupRef.current.forEach((f) => { try { f(); } catch (e) { /* */ } }); docCleanupRef.current = []; } catch (e) { /* */ }   // MM 날짜배지 보정 해제(옵저버 + scroll 리스너)
    try { if (msgObsRef.current) { msgObsRef.current.disconnect(); msgObsRef.current = null; } } catch (e) { /* */ }   // #112-① 본문 zoom 옵저버 정리
  }, []);

  // 재시도: 동일 src를 강제 재로드 + 타이머 재시작
  const retryLoad = () => {
    setLoadErr(false);
    if (app === 'messenger') setMmProvisionFailed(false);
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setLoadErr(true), 20000);
    ssoTries.current = 0;
    teamTries.current = 0;
    pendingReauthRef.current = null;   // 사용자가 다시 시도하면 앞선 자동 재인증의 결과는 기다리지 않는다
    ncWaitRef.current = null;
    if (ref.current) { try { ref.current.src = src; } catch (e) { /* */ } }
  };

  // 로드 감시 타이머를 다시 건다 — SSO·인가 중간 문서에서 멈추면 20초 뒤 «불러올 수 없습니다»(무한 로딩 방지).
  const armLoadTimer = () => {
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setLoadErr(true), 20000);
  };

  // 외부 요청(모바일 하단 탭바 재탭)으로 iframe 만 강제 새로고침 — 상위 페이지(window.location)는 건드리지 않는다.
  // 재로드 규약은 retryLoad 와 같다(로드 감시 타이머·SSO/팀 카운터 리셋).
  // src 가 아직 없으면(첫 SSO 핸드셰이크 중) 무시한다. 입력 내용 확인·임시저장은 하지 않는다(조건 없이 새로고침).
  const reloadEmbed = () => {
    if (!src || !ref.current) return;
    setLoadErr(false);
    if (app === 'messenger') setMmProvisionFailed(false);
    setLoaded(false);                 // 로딩 인디케이터 재노출(onLoad 가 다시 해제한다)
    ssoTries.current = 0;
    teamTries.current = 0;
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setLoadErr(true), 20000);
    // 재로드하면 onLoad 가 새 document 에 관찰자를 다시 단다 → 버려질 옛 document 의 관찰자는 여기서 끊는다.
    // (재탭 반복이 이 기능의 정상 사용 패턴이라 안 끊으면 재탭마다 쌓인다. 언마운트 정리(F1)와 같은 방식.)
    try { obsRef.current.forEach((o) => { try { o.disconnect(); } catch (e) { /* */ } }); obsRef.current = []; } catch (e) { /* */ }
    try { docCleanupRef.current.forEach((f) => { try { f(); } catch (e) { /* */ } }); docCleanupRef.current = []; } catch (e) { /* */ }   // 옛 document 의 날짜배지 보정도 같이 끊는다
    try { ref.current.src = src; } catch (e) { return; }   // 재할당이 실패하면 아래 표식도 올리지 않는다(거짓 증거 방지)
    setReloadSeq((n) => n + 1);       // 관측용 — iframe 의 data-gw-reload-seq 로 노출(e2e/단위시험이 재로드를 확인)
  };

  // src 가 바뀌면 구독을 다시 걸어 항상 최신 src 로 재할당한다.
  useEffect(() => subscribeEmbedReload(app, reloadEmbed), [app, src]);

  // 딥링크 보존(🟢): 새로고침 시 iframe의 마지막 경로 복원(탭형 collab-tools·로그인/SSO/로그아웃 경로 제외).
  const embedBase = NC_APPS.includes(app) ? '/drive' : (app === 'messenger' ? '/mm' : (app === 'mail2' ? '/mail2' : null));
  const PATH_KEY = 'embedPath:' + app;
  // 폴더 위치만 복원 — 열린 파일/에디터(/f/123·richdocuments·directEditing·openfile=)는 제외(드라이브 열 때 문서 자동열림 방지).
  // ⚠️앱 자기 root(appRoot)로 제한 — embedBase(/drive 공통)로 하면 메일이 한번 files로 빠질 때 그 드라이브 경로가
  //   embedPath:mail 키에 저장돼 다음에 메일 눌러도 드라이브가 복원되는 버그(앱별 키인데 prefix가 너무 넓었음).
  // 메신저 영구링크(/pl/ — 알림으로 연 글)도 제외 — 저장하면 다음에 메신저를 열 때마다 그 옛 글로 튄다.
  const validEmbedPath = (sp) => !!sp && !!appRoot && sp.startsWith(appRoot)
    && !/\/login|oauth|user_oidc|logout/i.test(sp)
    && !(app === 'mail' && /\/apps\/mail\/setup/i.test(sp))
    && !(app === 'messenger' && /\/pl\//.test(sp))
    && !/\/f\/\d|richdocuments|directediting|openfile=/i.test(sp);

  const withMail2UserHint = (url, email) => {
    if (app !== 'mail2' || !url || !email) return url;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('_user', email);
      return u.origin === window.location.origin ? (u.pathname + u.search + u.hash) : u.toString();
    } catch (e) {
      const s = String(url);
      return s + (s.includes('?') ? '&' : '?') + '_user=' + encodeURIComponent(email);
    }
  };

  // ── 임베드 인증 만료 자동 재인증(docs/embed_auth_expiry.md ⑥) ──────────────────
  // 🔴 서비스별 자동 재인증은 guard(60초 쿨다운·10분 3회)를 통과할 때 1회뿐이고, 실패하면 원인 카드로 끝낸다
  //    (자동 재시도 없음 — «다시 시도» 만 guard 를 푼다). 재인증 뒤 다시 로그인 화면이면 retrySso 로 돌아가지
  //    않고 카드로 바꾼다(루프 방지).
  const clearAuthFail = () => { authFailRef.current = null; setAuthFail(null); };

  // 원인 안내. NC·MM 은 실패 카드, 메일2는 RC 로그인 폼이 화면에 있으면 그걸 가리지 않는 배너만 띄운다.
  // report=false = 보고는 다른 쪽(그 복구를 돌린 쪽)이 이미 했다.
  const showAuthFailure = (cause, reason, { report = true } = {}) => {
    pendingReauthRef.current = null;
    ncWaitRef.current = null;
    const service = serviceOf(app);
    if (service) reauthGuard.fail(service);
    const already = !!(authFailRef.current && authFailRef.current.cause === cause);
    if (service && !already && report) reportEmbedAuthEvent(service, 'reauth_fail', reason || causeReason(cause));
    authFailRef.current = { cause };
    setAuthFail({ cause });
    if (app === 'mail2') {
      let formShown = false;
      try { formShown = isLoginDocument('mail2', ref.current && ref.current.contentWindow); } catch (e) { formShown = false; }
      if (formShown) return;
    }
    if (loadTimerRef.current) { clearTimeout(loadTimerRef.current); loadTimerRef.current = null; }
    setLoaded(false);
    setLoadErr(true);
  };

  // iframe 을 dst 로 다시 보내고 그 결과(다음 load)를 기다린다. report=false 면 성공을 따로 보고하지 않는다
  // (EmbedHost 가 숨은 SSO 성공을 이미 보고하고 옛 문서만 다시 불러오라고 한 경우).
  const navigateFrame = (dst, reason, report) => {
    const iframe = ref.current;
    if (!iframe || !dst) return false;
    setLoadErr(false);
    setLoaded(false);
    clearAuthFail();
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setLoadErr(true), 20000);
    pendingReauthRef.current = { reason, report: report !== false };
    try {
      iframe.contentWindow.location.replace(dst);
    } catch (e) {
      try { iframe.src = dst; } catch (e2) { return false; }
    }
    return true;
  };

  // 옛 문서를 그 자리에서 다시 불러온다 — 지금 같은 출처 경로가 앱 경로면 그대로, 아니면 앱 root 로.
  const reloadInPlace = (reason, report) => {
    let dst = effRoot;
    try {
      const loc = ref.current && ref.current.contentWindow && ref.current.contentWindow.location;
      const p = loc ? String(loc.pathname || '') : '';
      if (validEmbedPath(p)) dst = p + String(loc.search || '');
    } catch (e) { /* 읽을 수 없으면(교차 출처) 앱 root 로 */ }
    return navigateFrame(dst, reason, report);
  };

  // NC 재인증 — 숨은 SSO 로 세션을 되살린 뒤 이 iframe 을 그 자리에서 다시 불러온다. 반환 = 무언가를 했는지
  // (재인증 시작·합류·원인 카드). quietCooldown 이면 쿨다운(60초 안 재시도)은 조용히 넘긴다(다시 열기·포커스).
  // 🔴 NC 앱들은 세션 쿠키 하나를 같이 쓴다 — 복구는 탭 안에서 한 번에 하나다(lib/embedAuth NC 복구 기록).
  //    이미 다른 길(열기 직전 확인·복귀 확인·다른 화면)이 돌리고 있으면 새로 띄우지 않고(guard·보고도 하지 않는다)
  //    그 결과를 기다렸다가, 그 사이 아무도 이 화면을 다시 불러오지 않았을 때만 한 번 다시 불러온다(claimNcReload).
  // 🔴 보이는 iframe 으로 SSO 를 다시 타지 않는다(예전 recoverIfDead·retrySso) — 그 길의 nc-session-reset 이
  //    숨은 SSO 가 막 심은 OIDC state·세션 쿠키를 지워 복구끼리 서로를 깨뜨렸다.
  const reauthNc = (why, { quietCooldown = false } = {}) => {
    const waiting = ncWaitRef.current;
    if (pendingReauthRef.current || (waiting && Date.now() - waiting.at < NC_WAIT_MAX_MS)) return true;   // 결과를 기다리는 중
    let recovery = currentNcRecovery();
    const own = !recovery;
    if (own) {
      if (quietCooldown && reauthGuard.check('nextcloud') === 'cooldown') return false;
      if (!reauthGuard.tryAcquire('nextcloud')) {
        // 막혔으면 카드로 끝낸다 — 이미 원인이 떠 있으면 그 원인을 그대로 둔다(같은 원인은 다시 보고하지 않는다).
        showAuthFailure(authFailRef.current ? authFailRef.current.cause : 'cooldown', 'cooldown');
        return true;
      }
      reportEmbedAuthEvent('nextcloud', 'expired', why);
      recovery = trackNcRecovery(ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: effRoot }));
    }
    setLoaded(false);
    const entry = { recovery, at: Date.now() };
    ncWaitRef.current = entry;
    recovery.then(() => {
      if (ncWaitRef.current !== entry) return;
      ncWaitRef.current = null;
      if (claimNcReload(recovery, app)) reloadInPlace(why, own);
    }, (err) => {
      if (ncWaitRef.current !== entry) return;
      ncWaitRef.current = null;
      // 남이 돌린 복구가 실패했으면 보고는 그쪽이 했다 — 여기서는 화면만 원인 카드로 바꾼다.
      showAuthFailure(classifyEmbedError(err), embedErrorReason(err), { report: own });
    });
    return true;
  };

  // 재인증 1회. verify=true 면 지금 로그인 화면일 때만 한다. 반환 = 무언가를 시작했는지
  // (false 면 호출부가 원인 카드로 바꿀지 정한다 — 빈 화면·무한 로딩으로 남기지 않기 위해).
  const reauthEmbed = (reason, opts = {}) => {
    const service = serviceOf(app);
    if (!service || !src) return false;
    const w = ref.current && ref.current.contentWindow;
    if (opts.verify && !(w && isLoginDocument(app, w))) return false;
    if (pendingReauthRef.current) return false;              // 이미 재인증 결과를 기다리는 중
    if (service === 'roundcube' && mail2ReadyRef.current === false) return false;   // 연동 안 한 사용자 = 로그인 화면이 정상
    const why = reason === 'gateway_login' ? 'gateway_session' : reason;
    if (service === 'nextcloud') return reauthNc(why);       // NC 는 탭 안 단일 복구로(guard·보고도 그 안에서)
    if (!reauthGuard.tryAcquire(service)) {
      // 막혔으면 카드로 끝낸다 — 이미 원인이 떠 있으면 그 원인을 그대로 둔다(같은 원인은 다시 보고하지 않는다).
      showAuthFailure(authFailRef.current ? authFailRef.current.cause : 'cooldown', 'cooldown');
      return true;
    }
    reportEmbedAuthEvent(service, 'expired', why);
    setLoaded(false);
    if (service === 'mattermost') {
      ensureGatewayToken()
        .then((ok) => {
          if (!ok) { showAuthFailure('gateway_session', 'gateway_session'); return; }
          if (!navigateFrame(ssoUrl, why, true)) showAuthFailure('unknown');
        })
        .catch(() => { showAuthFailure('unknown'); });
      return true;
    }
    // roundcube — 새 티켓(HttpOnly 쿠키, 120초)을 받아 아이디 힌트와 함께 다시 연다.
    issueMail2Ticket()
      .then((ticket) => {
        if (ticket === null) { showAuthFailure('server_unreachable', 'server_unreachable'); return; }
        mail2ReadyRef.current = ticket.ready;
        if (!ticket.ready) {
          // 연동 안 한 사용자 — 로그인 화면이면 그대로 둔다. 쓸 화면이 없으면(차단·게이트웨이 로그인) 원인 카드.
          if (why === 'blocked_frame' || why === 'gateway_session') showAuthFailure(why === 'blocked_frame' ? 'blocked_frame' : 'gateway_session', why);
          else setLoaded(true);
          return;
        }
        if (!navigateFrame(withMail2UserHint(effRoot, mail2EmailRef.current), why, true)) showAuthFailure('unknown');
      })
      .catch(() => { showAuthFailure('unknown'); });
    return true;
  };

  // 실패 카드·배너의 «다시 시도» — 사용자가 누를 때만 guard 를 푼다.
  const retryAfterAuthFail = () => {
    const service = serviceOf(app);
    if (service) reauthGuard.reset(service);
    pendingReauthRef.current = null;
    ncWaitRef.current = null;
    clearAuthFail();
    // 메일2는 새 티켓이 있어야 다시 연결된다(옛 티켓은 120초) — 못 받는 경우만 그대로 다시 불러온다.
    if (app === 'mail2' && reauthEmbed('manual_retry')) return;
    retryLoad();
  };

  // EmbedHost 의 인증 신호(복귀 확인·5초 감시·숨은 SSO 성공). 옛 값을 쓰지 않게 src 가 바뀌면 다시 구독한다.
  useEffect(() => subscribeEmbedAuth(app, (action, reason) => {
    if (action === 'reload') { if (src) reloadInPlace(reason || 'open_check', false); return; }
    if (action === 'reauth') { reauthEmbed(reason || 'visibility_check'); return; }
    reauthEmbed(reason || 'login_redirect', { verify: true });
  }), [app, src, effRoot, ssoUrl]);

  // 드라이브: 관리자 설정 AI 이름을 가져와 어시스턴트 모달 라벨을 그 이름으로 강제 치환.
  useEffect(() => {
    if (app !== 'drive') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => { if (d && d.collab_ai_name) aiNameRef.current = d.collab_ai_name; })
      .catch(() => {});
  }, [app]);

  useEffect(() => {
    let dead = false;
    const gw = JSON.parse(localStorage.getItem('user') || '{}');
    const storedMail2Email = app === 'mail2' && gw.email ? String(gw.email).trim() : '';
    const resolveMail2Email = () => {
      if (app !== 'mail2') return Promise.resolve('');
      const token = localStorage.getItem('token');
      if (!token) return Promise.resolve(storedMail2Email);
      const API = process.env.REACT_APP_API_URL || '';
      return fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => (d && d.email ? String(d.email).trim() : storedMail2Email))
        .catch(() => storedMail2Email);
    };
    // 메신저 알림을 눌러 왔으면(/messenger?post=<글 id> — 서비스워커가 문서를 새로 연다) 그 글을 연다.
    // 한 번만 읽고, 쓴 뒤에는 주소에서 지운다(새로고침마다 그 글로 다시 튀지 않게).
    const postId = app === 'messenger' && /^\/messenger(\/|$)/.test(window.location.pathname)
      ? mmPostIdFromSearch(window.location.search) : null;
    const goMatch = (mail2Email) => {
      if (dead) return;
      let dst = effRoot;
      if (postId) { dst = mmPermalink(appRoot, postId) || effRoot; stripMmPostParam(window); }
      else if (!tabs) { try { const sp = sessionStorage.getItem(PATH_KEY); if (validEmbedPath(sp)) dst = sp; } catch (e) { /* */ } }
      setSrc(withMail2UserHint(dst, mail2Email || storedMail2Email));
    };
    // 세션 없음·다른 사용자(switchUser)·확인 실패(.catch) 모두 여기로 온다 — 글이 있으면 SSO 뒤 그 글로(redirect_to).
    const goSso = () => {
      if (dead) return;
      if (postId) { setSrc(mmSsoUrlForPost(ssoUrl, appRoot, postId)); stripMmPostParam(window); return; }
      setSrc(ssoUrl);
    };
    // NC 는 "세션 없음" 으로 판정된 순간 남은 쿠키가 쓸모없다 — 그 쿠키가 오히려 흐름을 깨뜨리므로
    // SSO 시작 전에 지운다(예방). 다른 앱은 해당 없음.
    const goSsoClean = () => { resetNcCookies().then(() => goSso()); };
    // 다른 사용자 세션일 때만: NC는 옛 세션 로그아웃 후 SSO, MM은 SSO가 세션 전환.
    const switchUser = () => {
      if (NC_APPS.includes(app)) {
        fetch('/drive/', { credentials: 'include' }).then((r) => r.text()).then((html) => {
          const m = html.match(/data-requesttoken="([^"]+)"/);
          if (!dead) setSrc(m ? ('/drive/index.php/logout?requesttoken=' + encodeURIComponent(m[1])) : ssoUrl);
        }).catch(goSso);
      } else {
        goSso();
      }
    };
    if (app === 'messenger') {
      fetch('/mm/api/v4/users/me', { credentials: 'include' })
        .then((r) => {
          if (r.status === 401) return null;            // 세션 없음(신규) → 바로 SSO
          if (!r.ok) return Promise.reject();
          return r.json();
        })
        .then((d) => {
          if (d === null) { goSso(); return; }
          (d.email && gw.email && d.email.toLowerCase() === gw.email.toLowerCase()) ? goMatch() : switchUser();
        })
        .catch(goSso);
    } else if (NC_APPS.includes(app)) {
      fetch('/drive/ocs/v2.php/cloud/user?format=json', { headers: { 'OCS-APIRequest': 'true' }, credentials: 'include' })
        .then((r) => {
          if (r.status === 401 || r.status === 404) return null;  // 세션 없음(신규) → 바로 SSO
          if (!r.ok) return Promise.reject();
          return r.json();
        })
        .then((d) => {
          if (d === null) { goSsoClean(); return; }   // 세션 없음 → 낡은 쿠키 정리 후 SSO
          const uid = d && d.ocs && d.ocs.data && d.ocs.data.id;
          (uid && String(uid) === String(gw.id)) ? goMatch() : switchUser();
        })
        .catch(goSsoClean);
    } else if (app === 'mail2') {
      // 🔴 자동 로그인: iframe 을 열기 **전에** 게이트웨이에서 티켓을 받아 둔다.
      //    티켓은 HttpOnly 쿠키로만 오고(스크립트가 못 읽는다) Roundcube 플러그인이 서버끼리
      //    비번과 교환한다 — **비밀번호는 브라우저를 거치지 않는다.**
      //    티켓을 못 받아도(연동 안 함·기능 꺼짐·오류) 그냥 진행한다 = 평소 로그인 화면이 뜬다.
      //    응답의 ready 는 기억해 둔다 — false(연동 안 함)면 나중에 로그인 화면이 떠도 재인증하지 않는다.
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || '';
      const ticket = token
        ? fetch(`${API}/api/mail2/autologin`, {
          method: 'POST', credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => ((r && r.ok && typeof r.json === 'function') ? r.json().catch(() => null) : null))
          .then((j) => { mail2ReadyRef.current = (j && typeof j.ready === 'boolean') ? j.ready : undefined; })
          .catch(() => undefined)
        : Promise.resolve(undefined);
      Promise.all([ticket, resolveMail2Email()]).then(([, email]) => {
        mail2EmailRef.current = email || storedMail2Email;
        goMatch(email);
      });
    } else {
      goMatch();
    }
    return () => { dead = true; };
  }, [app, effRoot, ssoUrl]);

  // 메신저: MM 페이지 로드 후 팀 배정 보장(신규 첫 로그인 'Team Not Found' 해소).
  // MM 계정은 이 시점에 이미 JIT 생성됨 → 'added'면 iframe 1회 새로고침해 팀으로 진입.
  const retrySso = (w) => {
    if (ssoTries.current >= MAX_SSO_RETRIES) return false;
    ssoTries.current += 1;
    const delay = 500 * ssoTries.current;
    const t = setTimeout(() => {
      // 🔴 NC 는 낡은 쿠키를 그대로 둔 채 재시도하면 **같은 이유로 또 실패한다**(state 유실 403).
      //    SSO 로 들어가는 길이 셋이라(초기 진입·recoverIfDead·여기) 하나만 고치면 안 고친 길로 샌다.
      const go = () => { try { w.location.replace(ssoUrl); } catch (e) { /* */ } };
      if (NC_APPS.includes(app)) resetNcCookies().then(go);
      else go();
    }, delay);
    ssoTimersRef.current.push(t);
    return true;
  };

  const showMmProvisionFailure = () => {
    if (loadTimerRef.current) { clearTimeout(loadTimerRef.current); loadTimerRef.current = null; }
    setLoaded(false);
    setMmProvisionFailed(true);
    setLoadErr(true);
  };

  const reloadMmSsoAfterEnsure = (w) => {
    if (!ssoUrl) { showMmProvisionFailure(); return; }
    setLoadErr(false);
    setMmProvisionFailed(false);
    setLoaded(false);
    ssoTries.current = 0;
    const t = setTimeout(() => { try { w.location.replace(ssoUrl); } catch (e) { /* */ } }, 500);
    ssoTimersRef.current.push(t);
  };

  const ensureMmAccountOnce = (w) => {
    if (app !== 'messenger') return false;
    if (mmEnsureTriedRef.current) {
      if (!mmEnsureRunningRef.current) showMmProvisionFailure();
      return false;
    }
    const token = localStorage.getItem('token');
    if (!token) { showMmProvisionFailure(); return false; }
    mmEnsureTriedRef.current = true;
    mmEnsureRunningRef.current = true;
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/oauth/mm/ensure-account`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { ok: false, msg: r.status === 403 ? 'forbidden' : 'error' }))
      .then((d) => {
        if (d && d.ok) reloadMmSsoAfterEnsure(w);
        else showMmProvisionFailure();
      })
      .catch(() => { showMmProvisionFailure(); })
      .finally(() => { mmEnsureRunningRef.current = false; });
    return true;
  };

  const handleMmSessionFailure = (w) => {
    if (retrySso(w)) return true;
    return ensureMmAccountOnce(w);
  };

  const isMmErrorPage = (w) => {
    try {
      const doc = w && w.document;
      const txt = (doc && doc.body && doc.body.innerText ? doc.body.innerText : '').slice(0, 1200);
      return /찾을 수 없습니다|Team Not Found|오류|에러|Error/i.test(txt);
    } catch (e) {
      return false;
    }
  };

  const retryEnsureTeam = (w) => {
    const t = setTimeout(() => { try { ensureTeam(w); } catch (e) { /* */ } }, 1200);
    teamTimersRef.current.push(t);
  };

  const ensureTeam = (w) => {
    if (app !== 'messenger' || teamTries.current >= MAX_TEAM_TRIES) return;
    teamTries.current += 1;
    const token = localStorage.getItem('token');
    if (!token) return;
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/oauth/mm/ensure-team`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { status: r.status === 403 ? 'forbidden' : 'error' }))
      .then((d) => {
        const st = d && d.status;
        if (st === 'added') {
          try { w.location.replace('/mm/ourbox'); } catch (e) { /* */ }
        } else if (st === 'already') {
          if (isMmErrorPage(w)) { try { w.location.replace('/mm/ourbox'); } catch (e) { /* */ } }
        } else if (st === 'pending' || st === 'error') {
          retryEnsureTeam(w);
        }
      })
      .catch(() => { retryEnsureTeam(w); });
  };

  // 전체 hideCss(메뉴 숨김) — 비관리자만.
  function injectHideCss(doc) {
    if (!doc || !doc.head || !hideCss || doc.getElementById('gw-embed-css')) return;
    const st = doc.createElement('style');
    st.id = 'gw-embed-css';
    st.textContent = hideCss;
    doc.head.appendChild(st);
  }
  // 메신저 로딩 로고 숨김 — 관리자 포함 전원(조기 주입으로 깜빡임 제거).
  function injectLoadingHide(doc) {
    if (!doc || !doc.head || doc.getElementById('gw-loading-css')) return;
    const st = doc.createElement('style');
    st.id = 'gw-loading-css';
    st.textContent = '#initialPageLoadingScreen{display:none !important}';
    doc.head.appendChild(st);
  }
  // MM 로그아웃 항목 숨김 — 데스크톱 계정메뉴 + 모바일 메인메뉴 공통(텍스트 매칭=MM 버전/레이아웃 무관).
  // 게이트웨이가 세션을 관리하므로 MM 자체 로그아웃은 노출하지 않음(임베드 세션 깨짐·혼란 방지). 관리자 포함 전원.
  function hideMmLogout(doc) {
    if (!doc || !doc.body) return;
    try {
      const items = doc.querySelectorAll('#logout, [id="logout"], [role="menuitem"], #userAccountMenu li, [class*="Menu" i] li, .mobile-main-menu li, .dropdown-menu li, nav li, [class*="sidebar" i] li');
      items.forEach((el) => {
        const t = (el.textContent || '').trim();
        if (el.id === 'logout' || t === '로그아웃' || /^log\s*out$/i.test(t)) {
          const item = el.closest('li, [role="menuitem"]') || el;
          try { item.style.setProperty('display', 'none', 'important'); } catch (e) { /* */ }
        }
      });
    } catch (e) { /* */ }
  }
  // doc당 1회: 즉시 1회 + 메뉴가 클릭으로 나중에 열려도 잡도록 MutationObserver(디바운스). 데스크톱·모바일 공통.
  function setupMmLogoutHider(doc) {
    if (!doc || !doc.body || doc.__gwMmLogoutObs) return;
    doc.__gwMmLogoutObs = true;
    hideMmLogout(doc);
    const MO = (doc.defaultView && doc.defaultView.MutationObserver) || window.MutationObserver;
    let t = null;
    const obs = new MO(() => { if (!activeRef.current || t) return; t = setTimeout(() => { t = null; if (!activeRef.current) return; hideMmLogout(doc); }, 150); });
    obs.observe(doc.body, { childList: true, subtree: true });
    obsRef.current.push(obs);   // 언마운트 정리 대상(F1)
  }

  // ── 메신저 '읽음 표시' ─────────────────────────────────────────────────────
  // 설계: `C:\aigw-designs\patch_messenger_read_receipts.md`
  // MM 에 read receipt 이 없어서 게이트웨이가 `channelmembers.lastviewedat` 으로 집계한다.
  // 🔴 목록(.post-list__dynamic)은 react-virtualized 라 스크롤하면 글 요소가 통째로 다시
  //    만들어진다 → 한 번 주입하고 끝내면 라벨이 사라진다. **옵저버로 계속 다시 붙인다.**
  //    (6초 뒤 멈추는 기존 폴링 루프에 얹으면 안 되는 이유가 이것이다.)
  //    옵저버는 목록이 아니라 **doc.body** 에 건다 — 채널을 옮기면 목록 요소 자체가
  //    갈려서, 목록에 걸어 두면 그때 옵저버가 죽은 노드에 남는다.
  function postIdsIn(doc) {
    const out = [];
    try {
      doc.querySelectorAll('[data-testid="postView"]').forEach((el) => {
        if (el.classList && el.classList.contains('post--system')) return;
        const id = (el.id || '').replace(/^post_/, '');
        if (id) out.push(id);
      });
    } catch (e) { /* 로드 중 */ }
    return out;
  }

  function drawReadLabels(doc) {
    try {
      doc.querySelectorAll('[data-testid="postView"]').forEach((el) => {
        const id = (el.id || '').replace(/^post_/, '');
        const r = id && receiptsRef.current[id];
        let badge = el.querySelector(':scope > .gw-read-badge');
        if (!r) { if (badge) badge.remove(); return; }
        if (!badge) {
          badge = doc.createElement('button');
          badge.type = 'button';
          badge.className = 'gw-read-badge';
          // `.post` 가 position:relative 라 절대배치가 안전하다(MM 레이아웃 계산을 안 건드린다).
          // 🔴 **오른쪽 아래**에 둔다. 오른쪽 위는 MM 호버 메뉴(`col post-menu`)가 덮는 자리다 —
          //    실측(2026-08-24): 그 메뉴가 글 상단 -4~34px · 오른쪽 206px 폭 · z-index 6 이라
          //    위쪽에 두면 마우스를 올리는 순간 라벨이 가려져 **클릭 자체가 안 된다**(마스터 지적).
          //    z-index 는 메뉴보다 낮게 둔다 — 더 높이면 이번엔 우리가 MM 버튼을 막는다.
          //    실측한 글 높이가 112~288px 라 아래쪽은 그 메뉴와 겹치지 않는다.
          badge.style.cssText = 'position:absolute;right:14px;bottom:4px;z-index:5;border:none;'
            + 'background:none;padding:2px 4px;font-size:11.5px;color:#c6613f;cursor:pointer;'
            + 'font-weight:600;line-height:1.2';
          badge.addEventListener('click', (ev) => {
            ev.preventDefault(); ev.stopPropagation();
            const cur = receiptsRef.current[id];
            if (cur) setReceiptView(cur);
          });
          el.appendChild(badge);
        }
        const label = '읽음 ' + r.read_count;
        if (badge.textContent !== label) badge.textContent = label;
      });
    } catch (e) { /* 로드 중 */ }
  }

  function refreshReceipts(doc) {
    if (receiptOffRef.current || receiptBusyRef.current || !activeRef.current) return;
    const now = Date.now();
    // 화면에 보이는 글 중 아직 모르거나 오래된 것만 — 스크롤할 때마다 전량 재조회하지 않는다.
    const ids = postIdsIn(doc).filter((id) => {
      const r = receiptsRef.current[id];
      return !r || now - r.at > 15000;
    }).slice(0, 60);
    if (!ids.length) { drawReadLabels(doc); return; }
    receiptBusyRef.current = true;
    mmAPI.readReceipts(ids)
      .then((res) => {
        if (res.data && res.data.enabled === false) { receiptOffRef.current = true; return; }
        const got = (res.data && res.data.receipts) || {};
        Object.keys(got).forEach((id) => { receiptsRef.current[id] = { ...got[id], at: Date.now() }; });
        drawReadLabels(doc);
      })
      // 403(그 채널 비멤버)·502 는 조용히 넘긴다 — 라벨이 안 붙을 뿐 메신저는 그대로 쓴다.
      .catch(() => {})
      .finally(() => { receiptBusyRef.current = false; });
  }

  function setupReadReceipts(doc) {
    applyMmReadBadgeCss(doc);
    if (!doc || !doc.body || doc.__gwReadObs) return;
    doc.__gwReadObs = true;
    const MO = (doc.defaultView && doc.defaultView.MutationObserver) || window.MutationObserver;
    let t = null;
    const kick = () => {
      if (!activeRef.current || t) return;
      t = setTimeout(() => { t = null; if (activeRef.current) refreshReceipts(doc); }, 400);
    };
    kick();
    const obs = new MO(kick);
    obs.observe(doc.body, { childList: true, subtree: true });
    obsRef.current.push(obs);   // 언마운트 정리 대상(F1)
  }
  // 메신저 날짜 floating 배지 보정(req-20260928-111009-70f4ed) — 로직은 utils/mmFloatingDate.
  // doc 당 1회만 설치되고(setupMmFloatingDate 내부 가드), dispose 는 docCleanupRef 로 모아
  // 언마운트·reloadEmbed 에서 끊는다(옵저버·scroll 리스너 누수 방지).
  function setupFloatingDate(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmFloatingDate(doc, { isActive: () => activeRef.current });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 모바일 메신저 팝업(메뉴·모달·팝오버) 뒤로가기 — 로직은 utils/mmMenuBackClose.
  // 팝업이 열리면 최상위 history 에 한 칸 쌓고, 뒤로가기(popstate)로 «맨 위 팝업만» 닫는다.
  // doc 당 1회만 설치되고(setupMmMenuBackClose 내부 가드), dispose 는 docCleanupRef 로 모아
  // 언마운트·reloadEmbed 에서 끊는다(옵저버·popstate 리스너 누수 방지).
  function setupMenuBackClose(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmMenuBackClose(doc, {
        isActive: () => activeRef.current && backCloseOkRef.current,
        topWin: window,
      });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메신저 입력창 옆 스티커 버튼 — 주입 로직은 utils/mmSticker, 패널은 components/StickerPicker.
  // + 고른 스티커 미리보기(입력창 바깥 위 · MM 카드 숨김) — utils/mmSticker setupMmStickerStage.
  // doc 당 1회만 설치되고(setupMmStickerButton·setupMmStickerStage 내부 가드), dispose 는 docCleanupRef 로 모아
  // 언마운트·reloadEmbed 에서 끊는다(옵저버·타이머·주입 버튼·미리보기 정리). 둘은 각자 try — 하나가 터져도 다른 하나는 선다.
  function setupStickerButton(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmStickerButton(doc, { isActive: () => activeRef.current, onOpen: (rect) => setStickerAnchor(rect || {}) });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
    try {
      const offStage = setupMmStickerStage(doc, { isActive: () => activeRef.current });
      if (typeof offStage === 'function') docCleanupRef.current.push(offStage);
    } catch (e) { /* MM DOM 변동 — 무동작(MM 첨부 카드가 그대로 보일 뿐) */ }
  }
  // 메신저 «채널 파일» 목록·개수에서 스티커 첨부를 뺀다(10-01 마스터) — MM 의 fetch 응답만 고친다
  // (MM 데이터는 그대로). 로직은 utils/mmStickerFileFilter. win 당 1회(내부 가드) · dispose 는 docCleanupRef.
  function setupStickerFileFilter(doc) {
    const win = doc && doc.defaultView;
    if (!win) return;
    try {
      const off = setupMmStickerFileFilter(win, {
        isActive: () => activeRef.current,
        getStickerCount: (channelId) => mmAPI.stickerFileCount(channelId).then((r) => (r && r.data) || { count: 0 }),
      });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM 변동 — 무동작(스티커가 보일 뿐) */ }
  }
  // 메신저 이미지 미리보기 배율(%) 표시 — 로직은 utils/mmZoomLabel.
  // doc 당 1회만 설치되고(setupMmZoomLabel 내부 가드), dispose 는 docCleanupRef 로 모아
  // 언마운트·reloadEmbed 에서 끊는다(옵저버·타이머·라벨 정리).
  function setupZoomLabel(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmZoomLabel(doc, { isActive: () => activeRef.current });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메신저 그림 미리보기 손가락 동작(핀치 줌·확대 중 끌기·좌우로 밀어 다음/이전) — 로직은 utils/mmPreviewTouch.
  // MM 이 마우스(휠·끌기)·키보드(←/→)로만 받는 것을 같은 이벤트로 바꿔 넘긴다. doc 당 1회 · dispose 는 docCleanupRef.
  function setupPreviewTouch(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmPreviewTouch(doc, { isActive: () => activeRef.current });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메신저 프로필 팝업에 전화·직급·직책(다우) — 로직은 utils/mmProfileExtra. 공개 범위 = 메신저 권한 사용자(10-01 마스터).
  function setupProfileExtra(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmProfileExtra(doc, {
        isActive: () => activeRef.current,
        fetchExtra: (email) => mmAPI.profileExtra(email).then((r) => (r && r.data) || { found: false }),
      });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메신저 스레드 답글 요약줄의 «안읽은 메세지 +n» 배지·점 — 로직은 utils/mmThreadUnread. doc 당 1회 · dispose 는 docCleanupRef.
  // API 가 아직 없으면(404 · 목에 함수 없음) 조용히 무동작 — 지금 화면 그대로다.
  function setupThreadUnread(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmThreadUnread(doc, {
        isActive: () => activeRef.current,
        fetchCounts: (ids) => ((!mmAPI || typeof mmAPI.threadUnread !== 'function')
          ? Promise.reject(new Error('no api'))
          : mmAPI.threadUnread(ids).then((r) => (r && r.data) || { enabled: false })),
        // 읽음 응답은 enabled:false 가 «명시» 될 때만 끈다 — 빈 본문을 꺼짐으로 읽지 않게 {} 로 둔다.
        markRead: (rootId) => ((!mmAPI || typeof mmAPI.threadRead !== 'function')
          ? Promise.reject(new Error('no api'))
          : mmAPI.threadRead(rootId).then((r) => (r && r.data) || {})),
      });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메신저 입력창 엑셀 붙여넣기 «데이터 / 이미지» 선택 — 로직은 utils/mmExcelPaste.
  // 데이터 = 기존 마크다운 표 그대로, 이미지 = 표를 PNG 로 만들어 첨부 대기열. doc 당 1회 · dispose 는 docCleanupRef.
  function setupExcelPaste(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmExcelPaste(doc, { isActive: () => activeRef.current });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작(지금 붙여넣기 동작 그대로) */ }
  }
  // 메신저 미리보기 창의 docx·xlsx·pptx 에 «미리보기» 버튼 — 로직은 utils/mmOfficePreview, 모달은 components/OfficePreviewModal.
  // doc 당 1회 · dispose 는 docCleanupRef.
  function setupOfficePreview(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupMmOfficePreviewButton(doc, { isActive: () => activeRef.current, onOpen: (info) => setOfficePreview(info) });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* MM DOM 변동 — 무동작 */ }
  }
  // 메일 목록: 글자크기 + compact 해제 + (옵션)안읽은 것만 — 같은-오리진 iframe CSS 주입.
  function applyMailStyle(doc) {
    if (!doc || !doc.head) return;
    let st = doc.getElementById('gw-mail-style');
    if (!st) { st = doc.createElement('style'); st.id = 'gw-mail-style'; doc.head.appendChild(st); }
    const px = mailFontRef.current;
    // NC Mail 목록=.app-content-list, 행=.list-item/.envelope 계열(라이브 DOM 변동 시 D QA에서 보정).
    const ROW = '.app-content-list .list-item, .app-content-list-item, .envelope';
    let css = ROW + '{ font-size:' + px + 'px !important; line-height:1.15 !important; padding-top:3px !important; padding-bottom:3px !important; }';  // #122①(마스터): 행 더 촘촘 — A 실측=행52px는 min-height 아닌 텍스트2줄 line-height(18.2px)가 지배 → line-height 1.3→1.15
    // #122① A 실측: 발신자줄(.list-item-content)+제목줄(.line-two) 각 line-height가 행 높이 지배(min-height 잠금 없음) → 내부 텍스트 행간 직접 축소(행 52→~38px)
    css += ' .list-item-content, .line-two, .envelope__subtitle, .list-item-content__name{ line-height:1.15 !important; }';
    // #122① 행높이(마스터 확정): NcListItem `--list-item-height` 변수는 anchor에서 40px로만 먹어 부족 → 마스터가 DevTools로 `.list-item__anchor`에
    //  inline `height:20px` 직접 넣어 동작 검증함. 그래서 **anchor에 height를 직접 박아** 행 높이 조절(가장 확실). 숫자만 바꾸면 행 키 조절됨.
    var MAIL_ROW_H = 28;  // 메일 행 anchor 높이(px). 마스터 요청 시 이 숫자만 조절.
    css += ' .app-content-list .list-item__anchor{ height:' + MAIL_ROW_H + 'px !important; min-height:0 !important; }';
    // #126①(마스터): 날짜 그룹 헤더('지난 1시간'·'오늘'·'어제'…) 숨김. A 라이브측정=헤더 클래스=`.section-title`(우선순위 섹션헤더도 같은 클래스라 전 그룹헤더 숨김=플랫 리스트).
    css += ' .app-content-list .section-title{ display:none !important; }';
    // #127(마스터): 안읽음 파란 dot 제거(볼드 제목·안읽음 필터로 충분). A 라이브측정: dot=`__extra__indicator`인데 그 부모 `__extra`
    //  컨테이너가 안읽은 행에만 존재(읽은 행엔 없음)+margin 4/4 → dot만 숨기면 빈 컨테이너+여백이 남아 시간 위치가 읽음행과 12px 어긋남.
    //  → 컨테이너 `__extra` 자체를 숨겨 여백까지 제거(안읽음/읽음 시간 정렬 일치).
    css += ' .app-content-list .list-item-content__inner__details__extra{ display:none !important; }';
    // #128(마스터): 안읽은 메일 텍스트=볼드 + 완전검정. A 라이브측정: 안읽음 제목 기본색이 회색(rgb107)·발신자 rgb34 → 순검정(#000)으로.
    css += ' .app-content-list .envelope:not(.seen) .envelope__subtitle__subject__text, .app-content-list .envelope:not(.seen) .list-item-content__name, .app-content-list .envelope:not(.seen) .envelope__subtitle{ color:#000 !important; }';
    // #129(마스터): 맨 위 메일이 상단 검색바(.sticky-header h53·bottom103)에 13px 가려짐 → 리스트 상단여백으로 첫 행을 검색바 아래로(라이브: pad20=gap7 여유).
    css += ' .app-content-list{ padding-top:20px !important; }';
    // 메일 목록 스크롤 복구: 게이트웨이 상단 도구막대 안의 iframe 높이에서 NC Mail list wrapper가 auto min-height로 버티면
    // 목록 하단이 잘리고 휠/트랙패드 스크롤이 먹지 않는 케이스가 있어, list 계층에 명시적으로 min-height:0 + overflow-y:auto를 부여한다.
    css += ' #content,#content-vue,#app-content-vue,#app-content,.app-content{ min-height:0 !important; }';
    css += ' .app-content-list{ min-height:0 !important; height:100% !important; max-height:100% !important; box-sizing:border-box !important; overflow-y:auto !important; overflow-x:hidden !important; overscroll-behavior:contain !important; }';
    css += ' .app-content-list .app-content-list__content,.app-content-list__content{ min-height:0 !important; overflow:visible !important; }';
    // ⚠️#109 v3 교정(A, 라이브 NC Mail iframe DOM 직접 측정으로 확정 — 번들소스 추측 3회 실패 후):
    //  - 목록 발신자 아바타 실제 엘리먼트 = `span.avatardiv`(NcAvatar 컴포넌트). v0 `.envelope .app-content-list-item-icon`·
    //    v1 `.app-content-list-item-icon`·v2 `.item-avatar` 전부 오답(라이브 측정으로 .avatardiv 확정·display:none 시 40→0px 검증).
    //  - 2줄 분리=`.line-two` column(제목 .envelope__subtitle + 미리보기 .envelope__preview-text의 공통부모, 라이브 확인). 유지.
    //  - 제목 조기잘림: 아바타 제거 + subname/subtitle/subject full width로 가용폭 끝까지(라이브: 잘리는 제목 0개 검증).
    css += ' .envelope .avatardiv{ display:none !important; }';   // ① 발신자 아바타 제거(NcAvatar=.avatardiv, 라이브 DOM 확정)
    css += ' .line-two, .line-two.one-line{ display:flex !important; flex-direction:column !important; align-items:stretch !important; width:100% !important; }';  // ② 제목/미리보기 세로 분리(공통부모)
    css += ' .list-item-content__inner__subname{ width:100% !important; }';  // ②-a subname 컨테이너 full width
    css += ' .envelope__subtitle{ width:100% !important; flex:1 1 auto !important; }';  // ②-b 제목줄 full width
    css += ' .envelope__subtitle__subject{ flex:1 1 auto !important; min-width:0 !important; }';  // ②-c 제목이 가용폭 끝까지·진짜 끝에서만 ellipsis
    css += ' .envelope__subtitle__subject__text{ max-width:none !important; }';  // ②-d ⭐핵심: NC가 제목 텍스트 span에 max-width:300px 하드코딩→긴 제목 조기잘림. 풀어서 부모 가용폭 전체 사용(라이브: 300→775px·잘림0 검증)
    css += ' .list-item-content__name{ width:116px !important; min-width:116px !important; max-width:116px !important; flex:0 0 116px !important; }';  // ③ 발신자 이름 고정폭(마스터: 기본 max-width:40%→길면 늘어남 → 고정 116px, 초과 시 ellipsis. zyoon87@ourbo(106px)+… 기준·기존 overflow:hidden/ellipsis/nowrap 활용)
    // #112 ②(F/마스터 2026-06-22): 목록 기본=제목만(미리보기 숨김), 행 hover 시에만 3줄 미리보기 노출.
    // 셀렉터(.envelope 행·.envelope__preview-text 미리보기)는 #109에서 A가 라이브 DOM 측정으로 확정한 것(추측 아님).
    // hover UX=오버레이(position:absolute, top:100%)로 띄워 아래 행이 안 밀림(인라인 확장 시 행 점프 방지, F 권장).
    css += ' .envelope__preview-text{ display:none !important; -webkit-line-clamp:3 !important; }';   // 기본=미리보기 숨김(제목만). #122② NC 기본 clamp:2 캡 무력화(hover에서 3줄 보장)
    css += ' .envelope{ position:relative !important; }';            // 오버레이 위치 기준
    css += ' .envelope:hover .envelope__preview-text{ display:-webkit-box !important; -webkit-line-clamp:3 !important; -webkit-box-orient:vertical !important; overflow:hidden !important; white-space:normal !important; position:absolute !important; left:0 !important; right:auto !important; width:73% !important; min-width:240px !important; max-width:480px !important; top:100% !important; z-index:20 !important; margin-top:2px !important; padding:8px 11px !important; min-height:70px !important; background:#fff !important; border:1px solid #e5e7eb !important; border-radius:6px !important; box-shadow:0 7px 20px rgba(0,0,0,.16) !important; font-size:13px !important; line-height:1.4 !important; color:#374151 !important; }';  // #122②(마스터): hover 3줄 — A 실측 clamp:2가 원인(폭 아님). clamp 3 보장 + min-height 70px(3줄 공간=짧은 스니펫도 체감 3줄). 폭 73%/max480 유지(A: 480px서 113자=3줄)
    if (mailUnreadRef.current) css += ' .app-content-list .envelope.seen{ display:none !important; }';  // NC Mail 5.9.3: 읽음=.envelope.seen, 안읽음=.seen없음(D 라이브 QA 검증)
    st.textContent = css;
  }
  // #112-① 메일 본문 확대/축소 — NC Mail 본문은 reading-pane(.app-content-details) 안의 중첩 iframe `iframe.message-frame`으로
  // 렌더됨(A 라이브 측정: sandbox=null·srcdoc=false·contentDocument 접근 가능 → 최상위 NC doc CSS로는 본문에 안 닿아 별도 주입 필요).
  // 본문 doc body에 `zoom` 주입 = 제목·본문·작은글씨 상대비율 유지하며 비례 확대(font-size 균일 적용 금지=마스터 지시).
  // ⚠️ message-frame은 iframe-resizer(자동높이) 사용 → iframe 엘리먼트 transform:scale 금지(높이 충돌). 본문 body zoom 주입이 정답(리사이저가 높이 재계산).
  // message-frame은 메일 바꿀 때마다 재생성 → 아래 옵저버/폴링이 열릴 때마다 재호출. z=1.0이면 빈 css로 원복.
  function applyMsgZoom(doc) {
    if (app !== 'mail' || !doc) return;
    try {
      doc.querySelectorAll('iframe.message-frame').forEach((f) => {
        let fd;
        try { fd = f.contentDocument || (f.contentWindow && f.contentWindow.document); } catch (e) { return; }  // 만일의 cross-origin 방어
        if (!fd || !fd.head) return;
        const z = mailMsgZoomRef.current;
        const want = (z > 1) ? ('body{ zoom:' + z + ' !important; }') : '';
        const cur = fd.getElementById('gw-msg-zoom');
        if (!cur || cur.textContent !== want) {
          // #117 안전장치(A 권고): iFrameResizer(자동높이)는 기존 style의 textContent만 갱신(characterData)하면
          //   재계산을 건너뛰어 확대분이 클립될 수 있음(자동탭에선 핸드셰이크 미완으로 측정 불가=확증 불가) →
          //   값이 바뀔 때 style 노드를 remove→append(=childList mutation)로 교체해 iFrameResizer 내부 옵저버를 확실히 트립
          //   + contentWindow에 resize 이벤트로 이중 보강(무해·클립 없어도 부작용 0). 동일값이면 no-op(불필요 churn 방지).
          if (cur) { try { cur.remove(); } catch (e) { /* */ } }
          const st = fd.createElement('style'); st.id = 'gw-msg-zoom'; st.textContent = want; fd.head.appendChild(st);
          try { if (f.contentWindow) f.contentWindow.dispatchEvent(new Event('resize')); } catch (e) { /* */ }
        }
        // 본문 iframe이 늦게 로드되는 경우 대비 1회 load 바인딩(메일 바꿔 새 iframe 생기면 새 엘리먼트라 재바인딩됨)
        if (!f.dataset.gwZoomBound) { f.dataset.gwZoomBound = '1'; try { f.addEventListener('load', () => { try { applyMsgZoom(doc); } catch (e) { /* */ } }); } catch (e) { /* */ } }
      });
    } catch (e) { /* */ }
  }
  // 관리자 전용: 정리된 NC 헤더 우측에 'NC 원본 열기'(정리 전 raw 네이티브 NC를 새 창으로) 링크. 비관리자엔 미생성.
  // CSS 화이트리스트(#gw-nc-orig)에 포함돼 헤더 정리에도 살아남음. 새 창은 게이트웨이 오리진 기준 /drive/(=원본 NC).
  function addNcOriginLink(he, doc) {
    if (!isAdmin || !he || !doc || doc.getElementById('gw-nc-orig')) return;
    const a = doc.createElement('button');
    a.id = 'gw-nc-orig';
    a.type = 'button';
    a.title = '관리자: 정리 전 원본 Nextcloud 화면을 새 창으로 엽니다';
    a.textContent = 'NC 원본 열기';
    const s = a.style;
    // 색은 시안 F NC 피부 변수(utils/collabSkinNc) — 다크 OS 에서 피부가 꺼지면 기본값(옛 흰색)으로 돌아간다.
    s.display = 'flex'; s.alignItems = 'center'; s.background = 'transparent'; s.border = '1px solid var(--gw-nc-hdr-line, rgba(255,255,255,.45))';
    s.color = 'var(--gw-nc-hdr-fg, #fff)'; s.cursor = 'pointer'; s.fontSize = '12px'; s.fontWeight = '600';
    s.height = '30px'; s.padding = '0 10px'; s.margin = '0 6px'; s.borderRadius = '15px'; s.whiteSpace = 'nowrap';
    a.addEventListener('mouseenter', () => { a.style.background = 'var(--gw-nc-hdr-hover, rgba(255,255,255,.15))'; });
    a.addEventListener('mouseleave', () => { a.style.background = 'transparent'; });
    a.addEventListener('click', () => { try { window.open('/drive/', '_blank'); } catch (e) { /* */ } });
    he.insertBefore(a, he.firstChild);
  }

  function reformatMailDates(doc) {
    try {
      if (!doc || !doc.body) return;
      const w = doc.defaultView;
      if (!w) return;

      const pad = (n) => String(n).padStart(2, '0');
      const fmt = (dateInt) => {
        const ts = Number(dateInt);
        if (!Number.isFinite(ts) || ts <= 0) return '';
        const d = new Date(ts * 1000);
        if (!Number.isFinite(d.getTime())) return '';
        return `${pad(d.getFullYear() % 100)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      const ingest = (payload) => {
        const rows = Array.isArray(payload) ? payload : (payload && (payload.messages || payload.data || payload.items));
        if (!Array.isArray(rows) || rows.length === 0) return;
        const map = doc.__gwMailDateMap || {};
        rows.forEach((m) => {
          if (!m) return;
          const id = m.databaseId != null ? m.databaseId : m.id;
          if (id != null && m.dateInt != null) map[String(id)] = m.dateInt;
        });
        doc.__gwMailDateMap = map;
        apply();
      };
      const apply = () => {
        const map = doc.__gwMailDateMap || {};
        doc.querySelectorAll('.envelope[data-envelope-id]').forEach((row) => {
          const id = row.getAttribute('data-envelope-id');
          const text = id ? fmt(map[String(id)]) : '';
          if (!text) return;
          const el = row.querySelector('.list-item-content__inner__details__details');
          if (!el || el.textContent === text) return;
          el.textContent = text;
          el.setAttribute('title', text);
          row.setAttribute('data-gwdate', '1');
        });
      };

      if (!doc.__gwMailDateFetchHook && w.fetch) {
        const origFetch = w.fetch.bind(w);
        doc.__gwMailDateFetchHook = true;
        w.fetch = (...args) => {
          const p = origFetch(...args);
          try {
            const raw = args && args[0];
            const url = typeof raw === 'string' ? raw : (raw && raw.url) || '';
            if (/\/(?:drive\/)?apps\/mail\/api\/messages\?/i.test(url)) {
              p.then((res) => {
                try {
                  if (res && res.ok) res.clone().json().then(ingest).catch(() => {});
                } catch (e) { /* best-effort */ }
              }).catch(() => {});
            }
          } catch (e) { /* best-effort */ }
          return p;
        };
      }

      apply();

      const href = (doc.location && doc.location.href) || '';
      const box = href.match(/\/box\/(\d+)/);
      const mailboxId = box && box[1];
      const visible = Array.from(doc.querySelectorAll('.envelope[data-envelope-id]')).slice(0, 30);
      const needs = visible.some((row) => {
        const id = row.getAttribute('data-envelope-id');
        return id && !(doc.__gwMailDateMap && doc.__gwMailDateMap[String(id)]);
      });
      if (mailboxId && needs && !doc.__gwMailDateFetchPending && w.fetch) {
        doc.__gwMailDateFetchPending = true;
        const token = (w.OC && w.OC.requestToken) || (doc.querySelector('head[data-requesttoken]') && doc.querySelector('head[data-requesttoken]').getAttribute('data-requesttoken')) || '';
        const fetchDates = (id) => w.fetch(`/drive/apps/mail/api/messages?mailboxId=${encodeURIComponent(id)}&limit=30&sort=newest&view=singleton`, {
          credentials: 'include',
          headers: token ? { requesttoken: token } : {},
        });
        const fetchMissingThreads = () => {
          const missing = Array.from(doc.querySelectorAll('.envelope[data-envelope-id]')).slice(0, 30)
            .map((row) => row.getAttribute('data-envelope-id'))
            .filter((id) => id && !(doc.__gwMailDateMap && doc.__gwMailDateMap[String(id)]))
            .slice(0, 10);
          if (!missing.length) return Promise.resolve();
          doc.__gwMailDateThreadPending = doc.__gwMailDateThreadPending || {};
          return Promise.all(missing.map((id) => {
            if (doc.__gwMailDateThreadPending[id]) return Promise.resolve();
            doc.__gwMailDateThreadPending[id] = true;
            return w.fetch(`/drive/apps/mail/api/messages/${encodeURIComponent(id)}/thread`, {
              credentials: 'include',
              headers: token ? { requesttoken: token } : {},
            })
              .then((r) => (r && r.ok ? r.json() : null))
              .then((payload) => { if (payload) ingest(payload); })
              .catch(() => {});
          })).then(() => {});
        };
        fetchDates(mailboxId)
          .then((r) => (r && r.ok ? r.json() : null))
          .then((payload) => { if (payload) ingest(payload); })
          .then(() => {
            const stillMissing = Array.from(doc.querySelectorAll('.envelope[data-envelope-id]')).slice(0, 30).some((row) => {
              const id = row.getAttribute('data-envelope-id');
              return id && !(doc.__gwMailDateMap && doc.__gwMailDateMap[String(id)]);
            });
            if (!stillMissing || mailboxId === 'priority') return null;
            return fetchDates('priority')
              .then((r) => (r && r.ok ? r.json() : null))
              .then((payload) => { if (payload) ingest(payload); });
          })
          .then(() => fetchMissingThreads())
          .catch(() => {})
          .finally(() => { doc.__gwMailDateFetchPending = false; });
      }
    } catch (e) { /* best-effort */ }
  }

  // NC 앱 공통: 글로벌 헤더(#header) 잡메뉴 숨김 + 좌측 정적 라벨(드라이브 cleanupDriveHeader 헤더부분과 동일).
  function cleanupNcHeader(doc, info) {
    if (!doc || !doc.head || !info) return;
    applyNcFSkin(doc);   // 시안 F NC 피부 — 공통만(흰 머리·r5·13px · 다크 OS 에선 꺼짐). 드라이브 전용 규칙은 여기로 새면 안 된다.
    if (!doc.getElementById('gw-nchdr-css')) {
      const st = doc.createElement('style');
      st.id = 'gw-nchdr-css';
      st.textContent =
        '#header .header-start > *:not(#gw-hdr-label){display:none !important}' +
        '#header .header-end > *:not(#notifications):not(#gw-nc-orig){display:none !important}';
      doc.head.appendChild(st);
    }
    const hs = doc.querySelector('#header .header-start');
    if (hs && !doc.getElementById('gw-hdr-label')) {
      const lab = doc.createElement('div');
      lab.id = 'gw-hdr-label';
      lab.innerHTML = info.svg + '<span>' + info.label + '</span>';
      const s = lab.style;
      s.display = 'flex'; s.alignItems = 'center'; s.gap = '6px'; s.color = 'var(--gw-nc-hdr-fg, #fff)';
      s.fontWeight = '650'; s.fontSize = '15px'; s.paddingLeft = '8px'; s.whiteSpace = 'nowrap'; s.cursor = 'default';
      hs.insertBefore(lab, hs.firstChild);
    }
    addNcOriginLink(doc.querySelector('#header .header-end'), doc);   // 관리자 전용 'NC 원본 열기'
  }

  // 깜빡임 제거: 임베드 문서는 로드 중에도 일찍 존재 → src 직후부터 50ms 폴링으로 onLoad보다 먼저 적용.
  // 관리자는 숨김 미적용(전체 메뉴 노출). 드라이브 AI 이름 치환은 관리자 포함 전원.
  // 메일 글자크기/안읽음 변경 시: localStorage 저장 + 현재 iframe에 즉시 반영.
  useEffect(() => { if (app !== 'mail') return; try { localStorage.setItem('mailFontPx', String(mailFontPx)); } catch (e) { /* */ } try { const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document)); if (d) applyMailStyle(d); } catch (e) { /* */ } }, [app, mailFontPx]);
  useEffect(() => { if (app !== 'mail') return; try { localStorage.setItem('mailUnreadOnly', mailUnreadOnly ? '1' : '0'); } catch (e) { /* */ } try { const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document)); if (d) applyMailStyle(d); } catch (e) { /* */ } }, [app, mailUnreadOnly]);
  // #112-① 본문 확대 변경 시: localStorage 저장 + 현재 열린 본문 iframe에 즉시 재주입.
  useEffect(() => { if (app !== 'mail') return; try { localStorage.setItem('mailMsgZoom', String(mailMsgZoom)); } catch (e) { /* */ } try { const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document)); if (d) applyMsgZoom(d); } catch (e) { /* */ } }, [app, mailMsgZoom]);

  // #115 열린 메일 본문/제목 추출 — 본문은 중첩 iframe(message-frame).contentDocument에서(A #112 측정: 접근가능),
  //  제목은 reading-pane(.app-content-details) 헤더에서 best-effort(없으면 빈값=본문만으로 동작). DOM 추출(백엔드 IMAP 불필요).
  const extractOpenMail = () => {
    let body = '', subject = '';
    try {
      const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
      if (!d) return { body, subject };
      const f = d.querySelector('iframe.message-frame');
      if (f) {
        try {
          const fd = f.contentDocument || (f.contentWindow && f.contentWindow.document);
          if (fd && fd.body) {
            // 라이브 innerText(줄바꿈 정상)를 받고, #115 후속 인용트림: 인용/서명 노드 위치에서 텍스트를 컷(라이브 DOM은 미변형=사용자 화면 보존).
            // 인용 경계는 A 라이브 측정 확정: blockquote가 핵심(Daou/Outlook/표준 회신 공통), Gmail은 .gmail_quote 등. 실측 예: 7557자 중 신본문 2508자(67%가 인용).
            body = (fd.body.innerText || '').trim();
            const qSel = 'blockquote, .gmail_quote, .moz-cite-prefix, #divRplyFwdMsg, .OutlookMessageHeader, .gmail_signature';
            const q = fd.body.querySelector(qSel);
            if (q) {
              const qText = (q.innerText || '').trim();
              const probe = qText.slice(0, 60);
              if (probe.length >= 20) {
                const idx = body.indexOf(probe);
                if (idx >= 30) body = body.slice(0, idx).trim();   // 신본문이 충분히 남을 때만 컷(못 찾거나 너무 짧으면 전체 유지=안전)
              }
            }
          }
        } catch (e) { /* */ }
      }
      // 제목 best-effort(셀렉터 후보 여러개 — 라이브 변동 대비, 실패해도 본문만으로 요약/초안 동작)
      try {
        const det = d.querySelector('.app-content-details');
        const sEl = det && (det.querySelector('.mail-message-header .subject, .envelope__subject, h1, [class*="subject"]'));
        if (sEl) subject = (sEl.innerText || sEl.textContent || '').trim().slice(0, 300);
      } catch (e) { /* */ }
    } catch (e) { /* */ }
    return { body, subject };
  };
  // #115 메일 AI 실행(요약/초안) — 게이트웨이 API 호출(EmbedPage 기존 raw fetch 관습), 결과는 게이트웨이측 패널에 표시.
  const runMailAi = async (mode) => {
    const { body, subject } = extractOpenMail();
    if (!body) { setAiPanel({ mode, error: '열린 메일 본문을 찾지 못했습니다. 메일을 먼저 열어 주세요.' }); return; }
    setAiCopied(false);
    setAiPanel({ mode, loading: true });
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || '';
      const ep = mode === 'summary' ? '/api/mail-ai/summarize' : '/api/mail-ai/draft';
      const res = await fetch(`${API}${ep}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ subject, body }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || '요청에 실패했습니다.');
      // warn: 반증 ⚠ 한 줄(요약만 · 본문 밖) — copyAiText 는 text 만 복사한다.
      setAiPanel({ mode, text: (mode === 'summary' ? j.summary : j.draft) || '', warn: mode === 'summary' ? (j.falsify_warn || '') : '' });
    } catch (e) { setAiPanel({ mode, error: String((e && e.message) || e) }); }
  };
  const copyAiText = () => { try { navigator.clipboard.writeText(aiPanel && aiPanel.text ? aiPanel.text : '').then(() => { setAiCopied(true); setTimeout(() => setAiCopied(false), 1500); }).catch(() => {}); } catch (e) { /* */ } };

  // MM 자체 브라우저 알림(이 브라우저가 게이트웨이 푸시를 구독하지 않을 때 뜬다)을 누르면 — MM 처리기가 iframe 을
  // 그 글로 옮긴 뒤 — 게이트웨이도 메신저 화면으로 옮긴다(다른 화면이면 메신저 iframe 이 숨어 있어 안 바뀌어 보였다).
  // 🔴 이 화면엔 라우터 훅이 없다(라우터 없이 그리는 시험이 많다) → pushState + popstate 로 BrowserRouter 가 따라오게 한다.
  //    쿼리는 붙이지 않는다(MM 처리기가 이미 그 글로 옮겼다). 메인·옆 칸에 이미 보이면(activeRef) 포커스만 준다.
  const showMessengerFromMmNotification = () => {
    try { window.focus(); } catch (e) { /* */ }
    if (activeRef.current) return;
    try {
      window.history.pushState(null, '', '/messenger');
      window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
    } catch (e) { /* */ }
  };

  useEffect(() => {
    if (!src || !['drive', 'messenger', 'mail', 'talk', 'calendar', 'mail2'].includes(app)) return undefined;
    let n = 0;
    const id = setInterval(() => {
      if (!activeRef.current) return;
      n += 1;
      try {
        const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
        if (d) {
          if (app === 'drive') { cleanupDriveHeader(d); }   // 관리자 포함 전원 정리(cleanupDriveHeader가 AI이름 치환도 호출)
          else if (app === 'messenger') { injectLoadingHide(d); injectHideCss(d); applyMmFSkin(d, gwMidMatches()); applyMmModalCss(d); applyMmPostEditCss(d); applyMmStickerSizeCss(d); applyMmStickerOrderCss(d); setupMmLogoutHider(d); setupReadReceipts(d); setupFloatingDate(d); setupMenuBackClose(d); setupStickerButton(d); setupZoomLabel(d); setupPreviewTouch(d); setupProfileExtra(d); setupThreadUnread(d); setupStickerFileFilter(d); setupExcelPaste(d); setupOfficePreview(d); muteMmNotifications(d, showMessengerFromMmNotification); watchMmReadCalls(d); }   // 관리자 포함 전원(MM 제품/설정메뉴 불필요) + 시안 F 피부(색·크기·1100 채널 목록 190) + 로그아웃 숨김(모바일 포함) + 모바일 모달 헤더↔본문 여백 + 모바일 날짜배지 보정 + 모바일 팝업 뒤로가기 + 모바일 편집 버튼 한 줄 + 스티커 버튼 + 스티커 표시 120px + 스티커를 글 위로 + 이미지 배율 표시 + 미리보기 손가락 동작 + 프로필 전화·직급·직책 + 채널 파일 스티커 제외 + 엑셀 붙여넣기 선택 + 오피스 미리보기 버튼 + MM 자체 알림 음소거(푸시 구독·보는 중) + 읽음 API 감지 → 사이드바 안읽음 점 즉시 갱신
          else if (app === 'mail') { applyMailStyle(d); applyMsgZoom(d); reformatMailDates(d); cleanupNcHeader(d, NC_HDR.mail); }
          else if (app === 'talk') { cleanupNcHeader(d, NC_HDR.talk); }
          else if (app === 'calendar') { cleanupNcHeader(d, NC_HDR.calendar); }
          else if (app === 'mail2') { applyRcFSkin(d); }   // 시안 F 메일(RC) 피부 — 바깥 문서 + 읽기 창(#messagecontframe) 안쪽 문서 · 다크 OS 에선 꺼짐
        }
      } catch (e) { /* 로드 중 접근불가 — 무시 */ }
      if (n > 120) clearInterval(id); // ~6초 후 중단
    }, 50);
    return () => clearInterval(id);
  }, [app, src]);

  // 시안 F 메신저 피부(#2014): 게이트웨이 창 폭이 1100 구간(901~1200)을 드나들면 채널 목록 폭(216↔190)을 다시 판정한다.
  // 위 6초 폴링이 끝난 뒤에도 창 크기를 바꿀 수 있어 matchMedia change 로 건다. 접근 실패(로드 중)는 무시 · 언마운트 때 해제.
  useEffect(() => {
    if (app !== 'messenger' || typeof window.matchMedia !== 'function') return undefined;
    let mql = null;
    try { mql = window.matchMedia(GW_MID_QUERY); } catch (e) { mql = null; }
    if (!mql) return undefined;
    const onChange = (e) => {
      try {
        const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
        if (d) applyMmFSkin(d, !!(e && e.matches));
      } catch (err) { /* 로드 중 접근불가 — 무시 */ }
    };
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);   // Safari < 14 폴백
    return () => {
      try {
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
        else if (typeof mql.removeListener === 'function') mql.removeListener(onChange);
      } catch (e) { /* */ }
    };
  }, [app]);

  // 어시스턴트 모달의 'Nextcloud Assistant/어시스턴트' 라벨(제목+작성자)을 관리자 설정 이름으로 치환.
  function renameAssistantLabels(doc) {
    const name = aiNameRef.current;
    if (!doc || !doc.body || !name) return;
    const labels = ['Nextcloud Assistant', 'Nextcloud 어시스턴트', '아워박스 AI', BOT_NAME].filter((k) => k !== name);
    if (!labels.length) return;
    const walker = doc.createTreeWalker(doc.body, 4, null); // 4 = SHOW_TEXT
    const hits = [];
    let node;
    while ((node = walker.nextNode())) {
      const v = node.nodeValue;
      if (v && labels.some((k) => v.indexOf(k) !== -1)) hits.push(node);
    }
    hits.forEach((n) => { let v = n.nodeValue; labels.forEach((k) => { v = v.split(k).join(name); }); n.nodeValue = v; });
  }

  // doc당 1회: 초기 치환 + 모달이 나중에 열려도 잡도록 MutationObserver(디바운스).
  function setupAssistantRename(doc) {
    if (!doc || !doc.body || doc.__gwAiObs) return;
    doc.__gwAiObs = true;
    renameAssistantLabels(doc);
    const MO = (doc.defaultView && doc.defaultView.MutationObserver) || window.MutationObserver;
    let t = null;
    const obs = new MO(() => { if (!activeRef.current || t) return; t = setTimeout(() => { t = null; if (!activeRef.current) return; renameAssistantLabels(doc); }, 150); });
    obs.observe(doc.body, { childList: true, subtree: true, characterData: true });
    obsRef.current.push(obs);   // 언마운트 정리 대상으로 등록(F1)
  }

  // 드라이브 Files 앱 좌측 내비게이션 잡항목 숨김(텍스트 매칭). 유지: 모든 파일·공유·아워박스·삭제된 파일.
  function cleanupDriveNav(doc) {
    const nav = doc.querySelector('#app-navigation-vue, nav.app-navigation, .app-navigation');
    if (!nav) return;
    // ⚠️'개인 파일'은 제외 — l10n로 All files(files/files, 유지)와 Personal(files/personal, 숨김)이 둘 다
    // '개인 파일'로 보여 텍스트매칭이 All files까지 잘못 숨김. files/personal은 위 href-CSS(:has)가 정확히 숨김.
    const HIDE = ['최근', '즐겨찾기', '공유', '태그', '폴더 트리'];
    Array.prototype.forEach.call(nav.querySelectorAll('a, button'), (el) => {
      const txt = (el.textContent || '').trim();
      if (HIDE.indexOf(txt) !== -1) {
        const li = el.closest('li');
        (li || el).style.display = 'none';
      }
    });
    // 불필요 항목은 href-CSS(:has)가 즉시 숨기므로, nav 리스트 항목이 렌더되면 바로 ul 노출.
    // ('모든 파일' 텍스트 감지 의존 제거 — l10n로 라벨 바뀌면 영영 안 떠 4초 폴백까지 느렸던 원인)
    if (nav.querySelector('ul li')) Array.prototype.forEach.call(nav.querySelectorAll('ul'), (u) => { u.style.visibility = 'visible'; });
  }

  // 드라이브 오피스 파일(엑셀·PPT·워드 등)을 새 창으로 연다(10-01 마스터 «새창으로» · PC·모바일 모두) —
  // 로직은 utils/driveOfficeNewWindow. doc 당 1회만 설치되고(내부 가드), dispose 는 docCleanupRef 로
  // 모아 언마운트·reloadEmbed 에서 끊는다(캡처 리스너 누수 방지).
  // 🔴 noopener 링크로 연다(utils/openNoopener) — 처음부터 opener 가 없어야 편집기가 게이트웨이와 다른
  //    브라우저 프로세스로 뜬다(열고 나서 opener=null 로 끊으면 같은 프로세스에 남는다 · 10-06).
  //    반환값은 성공 여부(true/false) — window.open 에 'noopener' 를 주면 성공해도 null 이라 쓰지 않는다.
  function setupOfficeNewWindow(doc) {
    if (!doc || !doc.body) return;
    try {
      const off = setupDriveOfficeNewWindow(doc, {
        isActive: () => activeRef.current,
        openWindow: (url) => openInNewProcess(url),
        base: '/drive',
      });
      if (typeof off === 'function') docCleanupRef.current.push(off);
    } catch (e) { /* NC DOM 변동 — 무동작(같은 창에서 열림) */ }
  }

  // 드라이브(NC) 헤더 정리: 파일 앱 문서는 머리 줄을 없애고 [NC 원본 열기(관리자)] [AI] [종] 묶음을 본문 첫 줄 오른쪽 끝으로
  // 옮긴다(10-28 지윤 요청 · utils/driveHeaderActions). 그 밖 문서(드라이브 iframe 안 캘린더 등)는 머리 줄을 두고 묶음을
  // 머리 오른쪽에 둔다. 왼쪽 «파일» 라벨은 어느 문서에도 만들지 않는다(CSSOM 직접 조작=CSP 무관).
  function cleanupDriveHeader(doc) {
    if (!doc) return;
    applyNcFSkin(doc, { drive: true });   // 시안 F NC 피부 — 공통 + 드라이브 전용(파일 앱 문서 #content.app-files 일 때만)
    setupAssistantRename(doc);
    setupOfficeNewWindow(doc);
    // 1) <style> 주입(1회) — NC CSP style-src 'unsafe-inline' 허용 → 파싱 직후 적용=깜빡임 제거.
    //    헤더: 좌측은 전부 숨김/우측은 알림벨+묶음(#gw-drive-actions)만. 좌측 nav 리스트는 정리 전까지 숨김(JS가 잡항목 제거 후 노출).
    //    안전장치: JS가 못 잡아도 4초 뒤 애니메이션으로 자동 노출.
    if (doc.head && !doc.getElementById('gw-drive-css')) {
      const st = doc.createElement('style');
      st.id = 'gw-drive-css';
      st.textContent =
        '#header .header-start > *{display:none !important}' +
        '#header .header-end > *:not(#notifications):not(#gw-drive-actions){display:none !important}' +
        '#app-navigation-vue ul,nav.app-navigation ul{visibility:hidden;animation:gwNavShow 0s 0.8s forwards}' +
        '@keyframes gwNavShow{to{visibility:visible}}' +
        // 견고화(2.5a): 좌측 nav 서브뷰를 href(view-id, l10n 무관)로 CSS 숨김 → 렌더 즉시 적용=깜빡임 제거.
        // '모든 파일'(files/files)은 미매칭이라 유지. JS 텍스트매칭(cleanupDriveNav)은 backstop.
        ['files/recent', 'files/favorites', 'files/personal', 'files/sharing', 'files/systemtags', 'files/tags', 'folder-tree']
          .map((v) => `#app-navigation-vue li:has(a[href*="${v}"])`).join(',') + '{display:none !important}';
      doc.head.appendChild(st);
    }
    cleanupDriveNav(doc);
    // 2) [NC 원본 열기(관리자)] [AI] [종] 묶음 — 매번 불러도 멱등(배치만 다시 본다). 첫 줄을 Vue 가 다시 그려도 되돌리는
    //    옵저버는 doc 당 1회 설치되고, dispose 는 docCleanupRef 로 모아 언마운트·reloadEmbed 에서 끊는다.
    const off = setupDriveActions(doc, { isAdmin, botName: BOT_NAME, iconSvg: AI_BUTTON_ICON, openOrigin: () => { try { window.open('/drive/', '_blank'); } catch (e) { /* */ } }, isActive: () => activeRef.current });
    if (typeof off === 'function') docCleanupRef.current.push(off);
  }

  const onLoad = () => {
    if (loadTimerRef.current) { clearTimeout(loadTimerRef.current); loadTimerRef.current = null; }
    // onLoad는 SSO 리다이렉트·재로드로 여러 번 발화 → 직전 폴링 인터벌을 정리하고 새로 시작(F2: 중첩 누적 방지)
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (loadErr) setLoadErr(false);
    // 🔴 타이머를 지운 직후 프레임부터 본다 — 차단된 프레임은 아래 location 읽기에서 예외가 나 삼켜지고,
    //    loaded·loadErr 가 둘 다 false 로 남아 로딩 가림막이 영원히 떠 있었다. 게이트웨이 로그인 화면(`/login`)이
    //    iframe 안에 뜬 것도 여기서 잡는다. → 재인증 1회, 그 뒤에도 같으면(또는 못 하면) 원인 카드.
    const problem = detectFrameProblem(ref.current, app, { afterLoad: true });
    if (problem === 'blocked_frame' || problem === 'gateway_login') {
      const cause = problem === 'blocked_frame' ? 'blocked_frame' : 'gateway_session';
      if (pendingReauthRef.current || !reauthEmbed(problem)) showAuthFailure(cause);
      return;
    }
    try {
      const w = ref.current && ref.current.contentWindow;
      const doc = ref.current && (ref.current.contentDocument || (w && w.document));
      if (!w) return;
      const p = w.location.pathname || '';
      const isLogin = /\/login(\/|$|\?)/.test(p) || /index\.php\/login/.test(p);
      // 재인증 뒤에도 로그인 화면이면 retrySso 로 돌아가지 않고 원인 카드로 끝낸다(루프 방지).
      if (isLogin && pendingReauthRef.current) { showAuthFailure('auth_expired'); return; }
      // 메일2(RC) 로그인 화면은 경로가 `/mail2/` 그대로라 위 isLogin 에 안 걸린다 — 문서를 보고 판정한다.
      // 연동한 사용자(ready !== false)면 새 티켓으로 1회 재인증, 재인증 뒤에도 그대로면 배너로 안내한다.
      // RC 로그인 폼은 그대로 쓸 수 있게 두므로 return 하지 않는다(가림막 해제까지 아래로 흐른다).
      if (app === 'mail2' && isLoginDocument('mail2', w)) {
        if (pendingReauthRef.current) showAuthFailure('auth_expired');
        else if (mail2ReadyRef.current !== false) reauthEmbed('login_redirect');
      }
      // 시안 F 메일(RC) 피부 — 로그인 화면·받은편지함 모두. RC 화면 이동(메일↔주소록↔설정)은 문서 새로 고침이라 여기서 매번 다시 넣는다.
      if (app === 'mail2' && doc) applyRcFSkin(doc);
      if (NC_APPS.includes(app) && isLoginDocument(app, w)) {
        // 🔴 로드 결과가 '세션 죽음' 이면 **여기서** 복구를 건다.
        //    예전엔 살아 있을 때 카운터만 초기화하고, 죽었을 때는 아무것도 안 했다 —
        //    복구 검사가 창 포커스·탭 전환에만 걸려 있어서, 드라이브를 처음 눌러 깨진 사람은
        //    아무 일도 안 일어났다(2026-08-25 실측). 낡은 NC 쿠키가 남아 있으면 NC 가
        //    SameSite 보호 리다이렉트로 **깨진 주소**(`/drive/index.php_oidc/login/1`)를 주고
        //    거기서 멈춘다. 복구는 숨은 SSO(쿠키 정리 포함) → 그 자리에서 다시 불러오기 1회다
        //    (reauthNc — 탭 안 단일 복구 · 60초 쿨다운·10분 3회 상한 = 공용 guard).
        // 🔴 재인증·재로드 뒤에도 로그인 문서면 원인 카드로 끝낸다(retrySso 로 돌아가지 않는다 = 루프 없음).
        //    숨은 화면은 여기서 복구하지 않는다 — 다시 열 때(keepAliveActive·열기 직전 확인) 본다.
        if (pendingReauthRef.current) { showAuthFailure('auth_expired'); return; }
        if (activeRef.current) reauthNc('login_redirect');
        return;
      }
      if (app === 'messenger') { injectLoadingHide(doc); injectHideCss(doc); applyMmFSkin(doc, gwMidMatches()); applyMmModalCss(doc); applyMmPostEditCss(doc); applyMmStickerSizeCss(doc); applyMmStickerOrderCss(doc); setupMmLogoutHider(doc); setupReadReceipts(doc); setupFloatingDate(doc); setupMenuBackClose(doc); setupStickerButton(doc); setupZoomLabel(doc); setupPreviewTouch(doc); setupProfileExtra(doc); setupThreadUnread(doc); setupStickerFileFilter(doc); setupExcelPaste(doc); setupOfficePreview(doc); muteMmNotifications(doc, showMessengerFromMmNotification); watchMmReadCalls(doc); }   // 관리자 포함 전원 + 시안 F 피부(색·크기·1100 채널 목록 190) + 로그아웃 숨김(모바일 포함) + 모바일 모달 헤더↔본문 여백 + 모바일 날짜배지 보정 + 모바일 팝업 뒤로가기 + 모바일 편집 버튼 한 줄 + 스티커 버튼 + 스티커 표시 120px + 스티커를 글 위로 + 이미지 배율 표시 + 미리보기 손가락 동작 + 프로필 전화·직급·직책 + 채널 파일 스티커 제외 + 엑셀 붙여넣기 선택 + 오피스 미리보기 버튼 + MM 자체 알림 음소거(푸시 구독·보는 중) + 읽음 API 감지(재로드·6초 넘는 SSO 로드 대비 onLoad 에서도)
      // 드라이브: 헤더 정리(비관리자) + AI 이름 치환(전원). Vue 늦은 렌더 대비 여러 번 재적용.
      if (app === 'drive') {
        const run = () => { try { const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document)); if (d) cleanupDriveHeader(d); } catch (e) { /* */ } };
        run();
        // 늦게 마운트되는 좌측 nav를 거의 즉시 잡도록 70ms 간격으로 ~6초 반복(깜빡임 최소화)
        let k = 0;
        const iv = setInterval(() => { if (!activeRef.current) return; run(); if (++k > 85) { clearInterval(iv); pollRef.current = null; } }, 70);
        pollRef.current = iv;
      }
      // 메일·화상회의: 헤더 정리(비관리자) + 메일 글자크기/안읽음 적용. Vue 늦은 렌더 대비 반복.
      if (app === 'mail' || app === 'talk' || app === 'calendar') {
        const run = () => { try {
          const d = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
          if (d) { if (app === 'mail') { applyMailStyle(d); applyMsgZoom(d); reformatMailDates(d); } cleanupNcHeader(d, NC_HDR[app]); }
        } catch (e) { /* */ } };
        run();
        let k = 0; const iv = setInterval(() => { if (!activeRef.current) return; run(); if (++k > 85) { clearInterval(iv); pollRef.current = null; } }, 70);
        pollRef.current = iv;
        // #112-① 메일 본문 zoom: 본문 iframe은 메일 열 때(6초 폴링 이후일 수 있음)·메일 바꿀 때마다 재생성 →
        //  NC doc body에 영구 MutationObserver를 걸어 message-frame이 등장/교체될 때마다 applyMsgZoom 재호출(100ms 디바운스).
        if (app === 'mail') {
          try {
            const dd = ref.current && (ref.current.contentDocument || (ref.current.contentWindow && ref.current.contentWindow.document));
            if (dd && dd.body) {
              try { if (msgObsRef.current) msgObsRef.current.disconnect(); } catch (e) { /* */ }   // onLoad 재발화 시 이전 옵저버 정리(중첩 누적 방지)
              let t = null;
              const mo = new MutationObserver(() => { if (!activeRef.current || t) return; t = setTimeout(() => { t = null; if (!activeRef.current) return; try { applyMsgZoom(dd); reformatMailDates(dd); } catch (e) { /* */ } }, 100); });
              mo.observe(dd.body, { childList: true, subtree: true });
              msgObsRef.current = mo;
            }
          } catch (e) { /* */ }
        }
      }
      if (app === 'mail' && /\/apps\/mail\/setup/i.test(p) && !mailSetupRedirected.current) {
        mailSetupRedirected.current = true;
        try { sessionStorage.removeItem(PATH_KEY); } catch (e) { /* */ }
        w.location.replace(effRoot);
        return;
      }
      if (isLogin) {
        if (app === 'messenger') handleMmSessionFailure(w);
        // NC 는 로그인 문서를 위에서 끝냈다 — 여기 오는 건 SSO 중간 문서(user_oidc/login/…)다. 보이는 iframe 으로
        // SSO 를 다시 타지 않고 다음 load 를 기다린다(20초를 넘기면 «불러올 수 없습니다»).
        else if (NC_APPS.includes(app)) armLoadTimer();
        // retrySso(최대 3회)를 다 쓰고도 로그인 화면이면 가림막에 갇히지 않게 원인 카드로 바꾼다.
        else if (!retrySso(w)) showAuthFailure('auth_expired');
        return;
      }
      if (app === 'messenger' && /^\/mm(\/|$)/.test(p) && !isLogin) {
        const txt = (doc && doc.body && doc.body.innerText ? doc.body.innerText : '').slice(0, 1200);
        if (/오류가 발생|에러|Error/i.test(txt)) {
          handleMmSessionFailure(w);
          return;
        }
        fetch('/mm/api/v4/users/me', { credentials: 'include' })
          .then((r) => {
            if (r.status === 401) {
              handleMmSessionFailure(w);
            }
          })
          .catch(() => { /* SSO 상태 전환 중 일시 오류 무시 */ });
      }
      // 로그인 페이지가 아닌 MM 페이지면(=세션 있음) 팀 배정 보장
      if (app === 'messenger' && /^\/mm(\/|$)/.test(p) && !isLogin) {
        ensureTeam(w);
      }
      // MM 자동 연결(#175 ②): 드라이브/메신저 로드(=세션 있음) 시 미연결이면 백그라운드로 OAuth 연결.
      if (!isLogin && (app === 'drive' || app === 'messenger')) autoConnectMattermost();
      // 딥링크 보존: 실제 콘텐츠 경로면 저장(탭형·로그인/SSO/로그아웃 제외) → 다음 새로고침 시 goMatch가 복원
      if (!tabs && !isLogin && validEmbedPath(p)) {
        try { sessionStorage.setItem(PATH_KEY, p + (w.location.search || '')); } catch (e) { /* */ }
      }
      // 실제 앱 콘텐츠가 떴으면 로딩 오버레이 해제(SSO/로그인/인가 중간단계 제외 → 깨진 빈 프레임 가림).
      if (!isLogin && embedBase && p.startsWith(embedBase) && !/user_oidc|oauth|authorize|logout/i.test(p)) {
        setLoaded(true);
        // 재인증 뒤 정상 문서가 떴다 = 회복. (EmbedHost 가 이미 보고한 재로드는 다시 보고하지 않는다.)
        const done = pendingReauthRef.current;
        if (done) {
          pendingReauthRef.current = null;
          const service = serviceOf(app);
          if (service) {
            reauthGuard.succeed(service);
            if (done.report) reportEmbedAuthEvent(service, 'reauth_ok', done.reason);
          }
          clearAuthFail();
        }
      } else {
        // SSO·인가 중간 문서(`/authorize`·user_oidc·oauth·logout) — 다음 load 를 기다린다. 🔴 위에서 타이머를 이미
        // 지웠으니 다시 건다 — 체인이 여기서 멈추면(예: 콜백 403) 가림막이 영원히 남던 자리다.
        armLoadTimer();
      }
    } catch (e) { /* 일시적/접근불가 — 무시 */ }
  };

  return (
    // 모바일: MobileScale이 <html>에 zoom(1.5×)을 걸어 100dvh가 1.5배(=화면 초과)로 스크롤 발생 →
    // MobileScale이 전역 제공하는 --gw-app-h(visualViewport.height÷zoom)로 정확히 한 화면을 채움.
    // 변수 없을 때(데스크톱·MobileScale 미머지)는 100dvh로 폴백(무회귀). (C feat/c-mobile-fit2 공유 패턴)
    <div style={{ flex: 1, width: '100%', height: '100%', minWidth: 0, minHeight: 0, background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Announcements scope={app} />
        {/* 읽음 표시 팝업 — MM iframe 밖(게이트웨이)이라 MM 버전업에 안 휘둘린다. */}
        {receiptView && <ReadReceiptModal data={receiptView} onClose={() => setReceiptView(null)} />}
        {stickerAnchor && <StickerPicker open onClose={() => setStickerAnchor(null)} iframeRef={ref} anchorRect={stickerAnchor} />}
        {officePreview && <OfficePreviewModal fileId={officePreview.fileId} fileName={officePreview.fileName} onClose={() => setOfficePreview(null)} />}
        {tabs && tabs.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0 }}>
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{ padding: '7px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                  background: activeTab === t.key ? COLORS.primary : 'transparent', color: activeTab === t.key ? 'white' : '#6b7280' }}>
                {t.label}
              </button>
            ))}
          </div>
        )}
        {app === 'mail' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>메일 글자 크기</span>
            <button onClick={() => setMailFontPx((p) => Math.max(12, p - 1))} title="작게" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>A−</button>
            <span style={{ fontSize: '13px', color: '#374151', minWidth: '38px', textAlign: 'center', fontWeight: 600 }}>{mailFontPx}px</span>
            <button onClick={() => setMailFontPx((p) => Math.min(22, p + 1))} title="크게" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>A+</button>
            <button onClick={() => setMailFontPx(14)} title="기본값" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>기본</button>
            <span style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
            <button onClick={() => setMailUnreadOnly((v) => !v)} title="안읽은 메일만 보기"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', border: '1px solid ' + (mailUnreadOnly ? COLORS.primary : '#d1d5db'), borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: mailUnreadOnly ? COLORS.primary : '#fff', color: mailUnreadOnly ? '#fff' : '#374151' }}>
              <IconMailOpened size={15} stroke={1.8} aria-hidden="true" />안읽은 것만{mailUnreadOnly ? ' ✓' : ''}</button>
            <span style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
            {/* #112-① 본문 확대/축소(비례 zoom) — 메일 본문 읽기 영역 전체를 상대비율 유지하며 확대 */}
            <span style={{ fontSize: '13px', color: '#6b7280' }}>본문 확대</span>
            <button onClick={() => setMailMsgZoom((z) => Math.max(1, Math.round((z - 0.1) * 10) / 10))} title="본문 축소" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>−</button>
            <span style={{ fontSize: '13px', color: '#374151', minWidth: '44px', textAlign: 'center', fontWeight: 600 }}>{Math.round(mailMsgZoom * 100)}%</span>
            <button onClick={() => setMailMsgZoom((z) => Math.min(2.5, Math.round((z + 0.1) * 10) / 10))} title="본문 확대" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>+</button>
            <button onClick={() => setMailMsgZoom(1)} title="기본값(100%)" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: '#374151' }}>기본</button>
            <span style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
            {/* #115 메일 AI 어시스트 — 열린 메일 본문 대상 요약/답장초안(생성·표시만, 자동발송 없음) */}
            <button onClick={() => runMailAi('summary')} title="열린 메일 본문을 AI가 요약합니다" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', border: `1px solid ${COLORS.primary}`, borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: COLORS.primary, color: '#fff' }}><IconSparkles size={15} stroke={1.8} aria-hidden="true" />AI 요약</button>
            <button onClick={() => runMailAi('draft')} title="이 메일에 대한 답장 초안을 AI가 작성합니다(발송은 직접)" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', border: `1px solid ${COLORS.primaryBorder}`, borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#fff', color: COLORS.text }}><IconPencil size={15} stroke={1.8} aria-hidden="true" />답장 초안</button>
          </div>
        )}
        {src ? (
          // 모바일: 사이드바가 하단 탭바이므로 iframe은 이미 전체폭 — 하단 탭바(60px · lib/tabbarMetrics TABBAR_H)에 안 가리게 gw-tabbar-pad로 여백 확보(데스크톱 무영향).
          // g2730 — 푸시 안내 띠(PushSetupBanner · fixed · z990)가 떠 있으면 그 높이(`--gw-push-banner-clear`)만큼 «임베드가 비켜 선다» — 띠를 숨기지 않는다.
          //   · 데스크톱: 아래 인라인 paddingBottom(변수 없으면 0px = 이전과 같다).
          //   · 모바일: 인라인은 MobileTabBar 의 `.gw-tabbar-pad.gw-embed-banner-pad` !important 규칙(max(탭바 몫, 변수) · 키보드 0)이 이긴다.
          //   · 키보드·녹음으로 띠가 display:none 이 되면 PushSetupBanner 가 변수를 지워 여백도 함께 풀린다.
          //   클래스 `gw-tabbar-pad` 는 그대로(r6_3ab6a706b0 이 `iframe.parentElement` 에서 이 이름을 찾는다) — `gw-embed-banner-pad` 는 «더 달기»다.
          //   absolute 요소(로딩·오류 덮개 · 메일 AI 패널)의 기준은 이 래퍼의 padding box 라 여백 띠까지 포함한다 → «바닥에 붙는» 것은
          //   bottom 에 같은 변수를 더해 띠 위로 올린다(위에 붙는 재로그인 띠는 영향 없음). 새 바닥 붙는 absolute 를 더하면 같은 식을 쓴다.
          <div className="gw-tabbar-pad gw-embed-banner-pad" style={{ position: 'relative', flex: 1, display: 'flex', minHeight: 0, paddingBottom: 'var(--gw-push-banner-clear, 0px)' }}>
            <iframe
              ref={ref}
              src={src}
              title={title}
              onLoad={onLoad}
              data-gw-reload-seq={reloadSeq}
              style={{ width: '100%', flex: 1, minHeight: 0, border: 'none', display: 'block' }}
              allow="clipboard-read; clipboard-write; microphone; camera; fullscreen"
            />
            {!loaded && !loadErr && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 'var(--gw-push-banner-clear, 0px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', background: '#fff', color: '#6b7280', fontFamily: 'var(--gw-font)', pointerEvents: 'none' }}>
                <div className="gw-spin" style={{ width: '34px', height: '34px', border: '3px solid #e5e7eb', borderTopColor: COLORS.primary, borderRadius: '50%' }} />
                <div style={{ fontSize: '14px' }}>{title} 불러오는 중…</div>
                <style>{`@keyframes gw-spin{to{transform:rotate(360deg)}}.gw-spin{animation:gw-spin .8s linear infinite}`}</style>
              </div>
            )}
            {loadErr && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 'var(--gw-push-banner-clear, 0px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', background: '#fff', color: '#6b7280', fontFamily: 'var(--gw-font)', textAlign: 'center', padding: '24px' }}>
                <IconAlertTriangle size={42} stroke={1.7} color="#b91c1c" aria-hidden="true" />
                {app === 'messenger' && mmProvisionFailed ? (
                  <div style={{ fontSize: '14px', maxWidth: '420px', lineHeight: 1.7, color: '#374151', fontWeight: 600 }}>{MM_PROVISION_MESSAGE}</div>
                ) : (
                  <>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>{withEulReul(title)} 불러올 수 없습니다.</div>
                    {authFail ? (
                      // 자동 재인증이 실패한 원인 한 줄(lib/embedAuth CAUSE_TEXT) — data 속성은 e2e 가 원인 종류를 읽는 자리다.
                      <div data-gw-embed-auth-cause={authFail.cause} style={{ fontSize: '13px', maxWidth: '360px', lineHeight: 1.6 }}>{causeText(authFail.cause)}</div>
                    ) : (
                      <div style={{ fontSize: '13px', maxWidth: '360px', lineHeight: 1.6 }}>서버가 응답하지 않거나 일시적인 오류일 수 있습니다. 잠시 후 다시 시도해 주세요.</div>
                    )}
                  </>
                )}
                {authFail ? (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
                    <button onClick={retryAfterAuthFail}
                      style={{ padding: '9px 22px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: COLORS.primary, color: '#fff' }}>
                      다시 시도
                    </button>
                    <button onClick={() => { relogin(); }}
                      style={{ padding: '9px 22px', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: '#fff', color: '#374151' }}>
                      다시 로그인
                    </button>
                  </div>
                ) : (
                  <button onClick={retryLoad}
                    style={{ marginTop: '4px', padding: '9px 22px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: COLORS.primary, color: '#fff' }}>
                    다시 시도
                  </button>
                )}
              </div>
            )}
            {/* 메일2: RC 로그인 폼을 직접 쓸 수 있어야 해서 가리지 않는 배너로만 안내한다. */}
            {app === 'mail2' && authFail && !loadErr && (
              <div role="status" data-gw-embed-auth-banner={authFail.cause}
                style={{ position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', zIndex: 20, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', maxWidth: 'calc(100% - 24px)', padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,.08)', fontFamily: 'var(--gw-font)', fontSize: '13px', color: '#9a3412' }}>
                <span>{causeText(authFail.cause)}</span>
                <button onClick={retryAfterAuthFail}
                  style={{ padding: '4px 10px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, background: COLORS.primary, color: '#fff' }}>다시 시도</button>
                <button onClick={() => { relogin(); }}
                  style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, background: '#fff', color: '#374151' }}>다시 로그인</button>
              </div>
            )}
            {/* #115 메일 AI 어시스트 결과 패널(요약/답장초안) — 우측 오버레이. 답장 자동발송 없음(복사만). */}
            {app === 'mail' && aiPanel && (
              <div style={{ position: 'absolute', top: '12px', right: '12px', bottom: 'calc(12px + var(--gw-push-banner-clear, 0px))', width: '380px', maxWidth: 'calc(100% - 24px)', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,.22)', display: 'flex', flexDirection: 'column', zIndex: 30, fontFamily: 'var(--gw-font)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #f1f1f4' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: COLORS.selectedText, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{aiPanel.mode === 'summary' ? <><IconSparkles size={16} stroke={1.8} aria-hidden="true" />AI 메일 요약</> : <><IconPencil size={16} stroke={1.8} aria-hidden="true" />답장 초안</>}</span>
                  <button onClick={() => setAiPanel(null)} title="닫기" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '0 2px' }}>×</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px', fontSize: '13px', lineHeight: 1.6, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                  {aiPanel.loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#6b7280', padding: '24px 0' }}>
                      <div className="gw-spin" style={{ width: '28px', height: '28px', border: '3px solid #e5e7eb', borderTopColor: COLORS.primary, borderRadius: '50%' }} />
                      <div>{aiPanel.mode === 'summary' ? '요약 생성 중…' : '답장 초안 작성 중…'}</div>
                    </div>
                  ) : aiPanel.error ? (
                    <div style={{ color: '#b91c1c' }}>{aiPanel.error}</div>
                  ) : (
                    <Markdown compact>{aiPanel.text || ''}</Markdown>
                  )}
                  {!aiPanel.loading && !aiPanel.error && aiPanel.warn && (
                    <div role="note" style={{ marginTop: '10px', fontSize: '12px', lineHeight: 1.5, color: '#b45309', padding: '6px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px' }}>{aiPanel.warn}</div>
                  )}
                </div>
                {!aiPanel.loading && !aiPanel.error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderTop: '1px solid #f1f1f4' }}>
                    <button onClick={copyAiText} style={{ padding: '7px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: COLORS.primary, color: '#fff' }}>{aiCopied ? '복사됨 ✓' : '복사'}</button>
                    {aiPanel.mode === 'draft' && (<span style={{ fontSize: '11px', color: '#9ca3af' }}>복사 후 메일 작성창에 붙여 넣어 검토·발송하세요(자동발송 없음).</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontFamily: 'var(--gw-font)' }}>불러오는 중…</div>
        )}
    </div>
  );
}

export default function EmbedPage({ splitSlot = 'main', ...props }) {
  const sideMode = splitSlot === 'side';
  return (
    <AppShell app={props.app} ownTabbarPad sidebarHidden={sideMode} sidebarActive={splitSlot === 'main'} style={{ width: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
      <EmbedContent {...props} />
    </AppShell>
  );
}
